/*
Description: This is the file that will handle interacting with the Raintree EMR
Date: 5/30/2024
Revamped: 2026-05 — Full Flex architecture, no hardcoded widget names
Author: Jacob Michalski
*/

export function getPageInfo() {
  const noteTypeSelect = document.querySelector(
    'select[name="notetypecode"]'
  ) as HTMLSelectElement | null;
  const noteType =
    noteTypeSelect?.options[noteTypeSelect.selectedIndex]?.textContent?.trim() ||
    noteTypeSelect?.value ||
    'Unknown';
  return { URL: window.location.origin, 'Note Type': noteType };
}

export function getPatientInfo() {
  const allLabels = Array.from(document.querySelectorAll('label[for=""]'));

  const nameLabel = allLabels.find((l) => l.textContent?.trim() === 'Patient');
  const dobLabel = allLabels.find((l) => l.textContent?.trim() === 'DOB');

  if (!nameLabel || !dobLabel) {
    console.error('Patient or DOB label not found');
    return null;
  }

  const patientNameEl = nameLabel
    .closest('.widget')
    ?.nextElementSibling?.querySelector('label');
  const patientDOBEl = dobLabel
    .closest('.widget')
    ?.nextElementSibling?.querySelector('label');

  if (!patientNameEl || !patientDOBEl) {
    console.error('Patient name or DOB element not found');
    return null;
  }

  const fullName = patientNameEl.textContent?.trim() || '';
  const spaceIdx = fullName.lastIndexOf(' ');
  const firstName = spaceIdx >= 0 ? fullName.slice(0, spaceIdx) : fullName;
  const lastName = spaceIdx >= 0 ? fullName.slice(spaceIdx + 1) : '';
  const emrID = `${lastName},${firstName}`.replace(/\s/g, '');

  return {
    'EMR ID': emrID,
    'First Name': firstName,
    'Last Name': lastName,
    DOB: patientDOBEl.textContent?.trim() || ''
  };
}

var questions = [];
var questionInteractions = {};
let getQuestionsInProgress = false;
let answeringInProgress = false;
let __answeringRefCount = 0;

// Cache the computed questions to avoid the expensive multi-tab rescan for rapid, back-to-back
// calls. Mirrors the WebPT2 injector: results are reused for GET_QUESTIONS_TTL_MS, and the TTL is
// refreshed while answering (see removeQuestionFromQueue) so we never rescan — and clobber
// in-progress fills — mid-answer.
let cachedQuestions: any[] | null = null;
let lastGetQuestionsAt = 0;
const GET_QUESTIONS_TTL_MS = 10000;

// Picker batch: Input() just queues targets; answerFlexQuestion flushes them all at once
const _pickerBatch = new Map<string, any[]>();
const _pickerBatchFlushers = new Map<string, () => Promise<void>>();

// Memo append deduplication: keyed by element ID, cleared at the start of each fill.
// Prevents the same text being appended multiple times when the same DOM element is
// registered by multiple phases (Phase 1 panel + Phase 4 flat) and the answer hits both.
const _memoAppendBase = new Map<string, string>(); // elementId → baseline before this fill
const _memoAppendedVals = new Map<string, string[]>(); // elementId → vals already appended

// Sub-tab registry: populated during GET_DOM Phase 2; used during fill to navigate nested tabs
// Key: "${topLevelKey}::${sectionKey}" → { widgetName, tabName }
const _subTabRegistry = new Map<
  string,
  { widgetName: string; tabName: string }
>();

// ─── Main Entry Point ─────────────────────────────────────────────────────────

export async function getQuestions() {
  // Serve cached results for rapid, back-to-back calls so we skip the expensive multi-tab rescan.
  const now = Date.now();
  if (cachedQuestions && now - lastGetQuestionsAt < GET_QUESTIONS_TTL_MS) {
    return cachedQuestions;
  }

  // Don't re-scan while answering — tab switches would wipe fills in progress. Serve the last good
  // scan regardless of the TTL window; the TTL is refreshed as each answer finishes
  // (removeQuestionFromQueue), so we keep serving cache right after answering too.
  if (answeringInProgress || __overlayAnswerQueue.length > 0) {
    return cachedQuestions ?? questions;
  }

  // Prevent concurrent scans — multiple GET_DOM messages would produce partial/mismatched question arrays
  if (getQuestionsInProgress) {
    while (getQuestionsInProgress) {
      await new Promise((r) => setTimeout(r, 100));
    }
    return cachedQuestions ?? questions;
  }
  getQuestionsInProgress = true;
  try {
    questions = [];
    questionInteractions = {};
    _subTabRegistry.clear();
    beginOverlay('Reading your note… Please wait.', { title: 'ComprehendPT' });

    const serviceTicketTab = document.querySelector(
      '[data-widgetname="servicetickettab"]'
    );
    if (!serviceTicketTab) {
      endOverlay();
      return [];
    }

    const tabContent = serviceTicketTab.querySelector('#tabcontent');
    if (!tabContent) {
      endOverlay();
      return [];
    }

    // Remember original tab to restore after scanning
    const originalTab = serviceTicketTab.querySelector(
      '.tab-item.active'
    ) as HTMLElement | null;

    // Click through every tab and capture its structure
    const allTabs = Array.from(
      serviceTicketTab.querySelectorAll('.tab-item[data-name]')
    ) as HTMLElement[];

    const ALLOWED_BASES = new Set([
      'subjective',
      'subj',
      'objective',
      'obj',
      'assess',
      'plan',
      'txplan',
      'progRec',
      'activities',
      'goals'
    ]);

    for (const tabEl of allTabs) {
      const tabName = tabEl.dataset.name || '';
      const tabBase = tabName.replace(/\d+$/, '');
      if (!tabName || !ALLOWED_BASES.has(tabBase)) continue;
      const topLevelKey = tabDataNameToSectionName(tabName);

      if (!tabEl.classList.contains('active')) {
        tabEl.click();
        await new Promise((r) => setTimeout(r, 600));
      }

      questionInteractions[topLevelKey] = {
        Type: 'Flex',
        Key: topLevelKey,
        Question: topLevelKey,
        tabName,
        Structure: {}
      };

      await buildAllPanels(tabContent, topLevelKey);
    }

    // Restore original tab
    if (originalTab && !originalTab.classList.contains('active')) {
      originalTab.click();
      await new Promise((r) => setTimeout(r, 400));
    }

    endOverlay();

    // Emit only non-empty Flex questions
    Object.keys(questionInteractions).forEach((key) => {
      const q = questionInteractions[key];
      if (q.Type !== 'Flex' || Object.keys(q.Structure || {}).length === 0)
        return;

      const outQuestion: any = {
        Key: key,
        Question: key,
        Type: 'Flex',
        Structure: {}
      };

      for (const [subsectionKey, fields] of Object.entries(
        q.Structure as Record<string, Record<string, any>>
      )) {
        const fieldEntries = Object.entries(fields);

        // Single-field subsection where the field key matches the subsection key
        // (e.g. "Goals" panel containing only a "Goals" listbox, or "Patient Goals" panel
        // containing only a "Patient Goals" memo) — flatten one level so the AI sees:
        //   "Goals": [...]  instead of  "Goals": { "Goals": [...] }
        if (
          fieldEntries.length === 1 &&
          normalizeKey(fieldEntries[0][0]) === normalizeKey(subsectionKey)
        ) {
          const def = fieldEntries[0][1];
          if (Array.isArray(def.Structure)) {
            outQuestion.Structure[subsectionKey] =
              (def.Structure as any[]) ?? [];
          } else {
            outQuestion.Structure[subsectionKey] = {
              ...(def['Existing Value'] != null && {
                _existingValue: def['Existing Value']
              }),
              ...(def.Choices && { _choices: def.Choices }),
              ...(def.Description && { _description: def.Description })
            };
          }
          continue;
        }

        // Skip subsections whose key duplicates the top-level question key,
        // BUT only when they contain no row-list fields (Structure arrays).
        // Pure Phase 4 flat-HTS catch-alls (e.g. "Subjective" inside Subjective,
        // "Objective" inside Objective) duplicate content already in named panels
        // and should be dropped. Row-list subsections (e.g. "Goals" inside Goals)
        // are the sole representation of that listbox and must be kept.
        if (
          normalizeKey(subsectionKey) === normalizeKey(key) &&
          !fieldEntries.some(([, def]) => Array.isArray((def as any).Structure))
        )
          continue;

        outQuestion.Structure[subsectionKey] = {};
        for (const [fieldKey, def] of Object.entries(fields)) {
          if (Array.isArray(def.Structure)) {
            outQuestion.Structure[subsectionKey][fieldKey] =
              (def.Structure as any[]) ?? [];
            continue;
          }
          outQuestion.Structure[subsectionKey][fieldKey] = {
            ...(def['Existing Value'] != null && {
              _existingValue: def['Existing Value']
            }),
            ...(def.Choices && { _choices: def.Choices }),
            ...(def.Description && { _description: def.Description })
          };
        }
      }

      outQuestion.Structure['flex++'] = true;
      questions.push(outQuestion);
    });

    cachedQuestions = questions;
    lastGetQuestionsAt = Date.now();
    return questions;
  } catch (err) {
    endOverlay();
    console.error('Error in getQuestions:', err);
    return [];
  } finally {
    getQuestionsInProgress = false;
  }
}

// ─── Panel → Structure Builder ────────────────────────────────────────────────

/**
 * Process all panels in a tab container, re-querying after each one that triggers
 * a DOM remount (e.g. picker screen navigation remounts panel elements).
 */
async function buildAllPanels(
  tabContent: Element,
  key: string,
  skipPhase2 = false
) {
  const processed = new Set<string>();

  // Pre-discover nested tab widgets so Phase 1 can skip their panels.
  // These are [data-widgetname] elements directly inside this tab content that contain
  // a .w-tab-control — Phase 2 handles them by activating each sub-tab in turn.
  // (The outer servicetickettab is NOT inside tabContent so it won't be matched here.)
  const _nestedWidgetNames = Array.from(
    tabContent.querySelectorAll('[data-widgetname]')
  )
    .filter(
      (el) =>
        !el.closest('.w-panel') && el.querySelector('.w-tab-control') !== null
    )
    .map((el) => el.getAttribute('data-widgetname'))
    .filter(Boolean) as string[];
  const _nestedWidgetSel = _nestedWidgetNames.length
    ? _nestedWidgetNames.map((w) => `[data-widgetname="${w}"]`).join(', ')
    : null;

  // Phase 1: .w-panel elements (standard panels with captions).
  // Exclude panels inside nested tab widgets — Phase 2 handles those when the sub-tab is
  // active so element refs are valid and fields are interactive.
  for (let guard = 0; guard < 30; guard++) {
    const panels = Array.from(tabContent.querySelectorAll('.w-panel')).filter(
      (panel) => !_nestedWidgetSel || !panel.closest(_nestedWidgetSel)
    );
    let processedAny = false;
    for (const panel of panels) {
      const name = panelCaptionName(panel);
      if (!name || processed.has(name)) continue;
      processed.add(name);
      await buildPanelStructure(panel, key);
      processedAny = true;
      if (!document.contains(panel)) break; // picker navigation remounted DOM
    }
    if (!processedAny) break;
  }

  // Phase 2: nested .w-tab-control widgets — skipped during fill rebuilds (stale element refs).
  // Auto-discovered so no hardcoding needed. Re-query after each tab scan (picker nav remounts DOM).
  if (!skipPhase2) {
    for (const widgetName of _nestedWidgetNames) {
      const widgetEl = () =>
        tabContent.querySelector(
          `[data-widgetname="${widgetName}"]`
        ) as HTMLElement | null;
      if (!widgetEl()) continue;

      // Collect tab names up front (before any DOM changes)
      const initialCtrl = widgetEl()!.querySelector(
        '.w-tab-control'
      ) as HTMLElement | null;
      if (!initialCtrl || initialCtrl.closest('.w-panel')) continue;

      const tabNames = (
        Array.from(
          initialCtrl.querySelectorAll('.w-tab-control-tabs .tab-item')
        ) as HTMLElement[]
      )
        .map(
          (t) =>
            t.getAttribute('aria-label')?.trim() ||
            t.querySelector('span')?.textContent?.trim() ||
            ''
        )
        .filter(Boolean);

      const originalActiveName =
        (initialCtrl.querySelector('.tab-item.active') as HTMLElement | null)
          ?.getAttribute('aria-label')
          ?.trim() || '';

      for (const tabName of tabNames) {
        if (processed.has(tabName)) continue;
        processed.add(tabName);

        // Re-query after potential DOM remount from previous tab's picker scan
        const freshCtrl = widgetEl()?.querySelector(
          '.w-tab-control'
        ) as HTMLElement | null;
        if (!freshCtrl) continue;

        const tabItem = (
          Array.from(
            freshCtrl.querySelectorAll('.w-tab-control-tabs .tab-item')
          ) as HTMLElement[]
        ).find(
          (t) =>
            (t.getAttribute('aria-label')?.trim() ||
              t.querySelector('span')?.textContent?.trim()) === tabName
        );

        // Activate tab if not already active
        if (tabItem && !tabItem.classList.contains('active')) {
          tabItem.click();
          await new Promise((r) => setTimeout(r, 400));
        }

        // Re-query again after tab click (Raintree may remount content)
        const freshCtrl2 = widgetEl()?.querySelector(
          '.w-tab-control'
        ) as HTMLElement | null;
        if (!freshCtrl2) continue;
        const tabContentEl = freshCtrl2.querySelector(
          '.w-tab-control-content'
        ) as HTMLElement | null;
        if (!tabContentEl) continue;

        // Snapshot structure keys before scanning this sub-tab so we can register new ones
        const structBefore = new Set(
          Object.keys(questionInteractions[key].Structure)
        );

        // Scan panels within this tab first.
        // Use a per-sub-tab set so identical panel names across different sub-tabs
        // (e.g. "ICD Codes" appearing in both "Diagnosis 1" and "Diagnosis 2") are
        // each scanned — the shared `processed` set must not block them.
        const tabPanelProcessed = new Set<string>();
        for (const panel of Array.from(
          tabContentEl.querySelectorAll('.w-panel')
        )) {
          const panelName = panelCaptionName(panel);
          if (!panelName || tabPanelProcessed.has(panelName)) continue;
          tabPanelProcessed.add(panelName);
          await buildPanelStructure(panel, key);
        }

        // Re-query tab content after panel scan (panels may contain picker listboxes)
        const freshCtrl3 = widgetEl()?.querySelector(
          '.w-tab-control'
        ) as HTMLElement | null;
        const freshTabContent = freshCtrl3?.querySelector(
          '.w-tab-control-content'
        ) as HTMLElement | null;
        if (!freshTabContent) continue;

        // Scan the tab content itself for fields not inside a panel
        await buildPanelStructure(freshTabContent, key, tabName);

        // Register all newly added structure sections in the sub-tab registry
        for (const sectionKey of Object.keys(
          questionInteractions[key].Structure
        )) {
          if (!structBefore.has(sectionKey)) {
            const rKey = `${key}::${sectionKey}`;
            _subTabRegistry.set(rKey, { widgetName, tabName });
          }
        }
      }

      // Restore original active tab
      const finalCtrl = widgetEl()?.querySelector(
        '.w-tab-control'
      ) as HTMLElement | null;
      if (finalCtrl && originalActiveName) {
        const restoreItem = (
          Array.from(
            finalCtrl.querySelectorAll('.w-tab-control-tabs .tab-item')
          ) as HTMLElement[]
        ).find(
          (t) => t.getAttribute('aria-label')?.trim() === originalActiveName
        );
        if (restoreItem && !restoreItem.classList.contains('active')) {
          restoreItem.click();
          await new Promise((r) => setTimeout(r, 300));
        }
      }
    }
  } // end if (!skipPhase2)

  // Phase 3: top-level listboxes directly in the tab content — not inside any .w-panel or
  // nested tab widget (those are already handled by Phase 1 & 2).
  const nestedWidgetSel = _nestedWidgetSel;
  const structure = questionInteractions[key].Structure;
  for (const listbox of Array.from(tabContent.querySelectorAll('.w-listbox'))) {
    if (listbox.closest('.w-panel')) continue;
    if (nestedWidgetSel && listbox.closest(nestedWidgetSel)) continue;
    if (listbox.querySelector('.w-listbox-scroller.disabled')) continue;
    const label = resolveLabel(listbox, tabContent, key);
    if (processed.has(label)) continue;
    processed.add(label);
    if (!structure[label]) structure[label] = {};
    const fields = structure[label];
    if (!buildCheckboxListboxField(listbox, tabContent, label, fields)) {
      if (!(await buildRowListField(listbox, tabContent, label, fields))) {
        await buildAddRowListboxField(listbox, tabContent, label, fields);
      }
    }
  }

  // Phase 4: flat HTS widget scan — handles absolute-positioned widgets directly in the
  // tab content. Runs when there are no panels (pure flat layout) OR when flat non-tab-control
  // widgets exist alongside nested sub-tab widgets (e.g. assessment complexity fields
  // next to the diagnosis sub-tab widget). Skip if all direct widgets are tab controls.
  const hasPanels = !!tabContent.querySelector('.w-panel');
  const flatContent =
    tabContent.querySelector(':scope > .w-panel-content') || tabContent;
  const hasFlatDirectWidgets = Array.from(
    flatContent.querySelectorAll(':scope > [data-widgetname]')
  ).some((w) => {
    const wEl = w as HTMLElement;
    const top = parseInt(wEl.style.top || '0');
    return (
      top >= 0 &&
      !wEl.querySelector('.w-tab-control') &&
      !!wEl.querySelector(
        '.w-combobox-openview, .w-memo-content, input[type="checkbox"], input[type="radio"]'
      )
    );
  });
  if (!hasPanels || hasFlatDirectWidgets) {
    buildFlatHtsStructure(flatContent, key);
  }
}

/**
 * Handle flat HTS tab layout: widgets positioned absolutely, no .w-panel structure.
 * Groups radio buttons by the nearest preceding standalone label above them.
 * Checkboxes are captured individually.
 */
function buildFlatHtsStructure(content: Element, key: string) {
  const structure = questionInteractions[key].Structure;
  if (!structure[key]) structure[key] = {};
  const fields = structure[key];

  const getTop = (w: HTMLElement) => parseInt(w.style.top || '0');
  const getLeft = (w: HTMLElement) => parseInt(w.style.left || '0');

  const allWidgets = Array.from(
    content.querySelectorAll(':scope > [data-widgetname]')
  ) as HTMLElement[];

  // Standalone labels: w-label whose <label> for= doesn't point to an input in this content
  type Anchor = { text: string; top: number; left: number };
  const anchors: Anchor[] = [];
  for (const w of allWidgets) {
    const labelEl = w.querySelector('.w-label');
    if (!labelEl) continue;
    const forId = w.querySelector('label')?.getAttribute('for') || '';
    const pointsToInput =
      forId &&
      !!content.querySelector(
        `input#${CSS.escape(forId)}, select#${CSS.escape(forId)}`
      );
    if (!pointsToInput) {
      const text = labelEl.textContent?.trim() || '';
      if (text) anchors.push({ text, top: getTop(w), left: getLeft(w) });
    }
  }

  // Return text of the nearest anchor above (and closest horizontally) to position (top, left)
  function nearestAnchor(top: number, left: number): string {
    let best = '';
    let bestScore = Infinity;
    for (const a of anchors) {
      if (a.top > top) continue;
      const score = top - a.top + Math.abs(a.left - left);
      if (score < bestScore) {
        bestScore = score;
        best = a.text;
      }
    }
    return best.replace(/[:\s]+$/, '') || key;
  }

  // Helper: skip widgets that are nested tab controls (handled by Phase 2)
  const isNestedTabWidget = (w: HTMLElement) =>
    !!w.querySelector('.w-tab-control');

  // Memos — each is its own field
  for (const w of allWidgets) {
    if (getTop(w) < 0 || isNestedTabWidget(w)) continue;
    const memo = w.querySelector('.w-memo-content') as HTMLElement | null;
    if (!memo) continue;
    const label = resolveLabel(memo, content, key);
    if (!label || label === key || fields[label]) continue;
    bubbleFocus(memo);
    fields[label] = {
      'Existing Value': (memo as HTMLElement).innerText?.trim() || null,
      Input: async (val: string, replyType: string) => {
        if (isEmptyOrNull(val)) return;
        const elId = (memo as HTMLElement).id || label;
        if (replyType === 'append') {
          const trimmed = val.trim();
          if (!_memoAppendBase.has(elId))
            _memoAppendBase.set(
              elId,
              (memo as HTMLElement).innerText?.trim() || ''
            );
          const existing = _memoAppendedVals.get(elId) ?? [];
          if (existing.includes(trimmed)) return;
          existing.push(trimmed);
          _memoAppendedVals.set(elId, existing);
          const base = _memoAppendBase.get(elId)!;
          await eventBasedFill(
            memo as HTMLElement,
            base ? `${base}\n${existing.join('\n')}` : existing.join('\n')
          );
        } else {
          await eventBasedFill(memo as HTMLElement, val);
        }
      }
    };
  }

  // Comboboxes — each is its own field
  for (const w of allWidgets) {
    if (getTop(w) < 0 || isNestedTabWidget(w)) continue;
    const el = w.querySelector(
      '.w-combobox-openview'
    ) as HTMLSelectElement | null;
    if (!el || el.disabled) continue;
    const label = resolveLabel(el, content, key);
    if (!label || label === key || fields[label]) continue;
    const optionMap = new Map<string, string>();
    Array.from(el.options).forEach((o) => {
      const txt = o.textContent?.trim() || '';
      if (txt) optionMap.set(txt, o.value);
    });
    const choices = Array.from(optionMap.keys());
    const existingText =
      el.selectedIndex >= 0
        ? el.options[el.selectedIndex]?.textContent?.trim() || el.value || null
        : el.value || null;
    fields[label] = {
      'Existing Value': existingText,
      Choices: choices,
      Input: (val: string) => {
        if (isEmptyOrNull(val)) return;
        const internalValue = optionMap.get(val) ?? val;
        bubbleFocus(el);
        el.focus();
        const wasReadonly = el.hasAttribute('readonly');
        if (wasReadonly) el.removeAttribute('readonly');
        const nativeSetter = Object.getOwnPropertyDescriptor(
          window.HTMLSelectElement.prototype,
          'value'
        )?.set;
        if (nativeSetter) nativeSetter.call(el, internalValue);
        else el.value = internalValue;
        el.dispatchEvent(new Event('input', { bubbles: true }));
        el.dispatchEvent(new Event('change', { bubbles: true }));
        const wrapper = el.closest('.w-combobox') as HTMLElement | null;
        if (wrapper)
          wrapper.dispatchEvent(new Event('change', { bubbles: true }));
        if (wasReadonly) el.setAttribute('readonly', '');
        el.blur();
      }
    };
  }

  // Checkboxes — each is its own field
  for (const w of allWidgets) {
    const el = w.querySelector(
      'input[type="checkbox"]'
    ) as HTMLInputElement | null;
    if (!el) continue;
    if (getTop(w) < 0 || isNestedTabWidget(w)) continue;
    const label =
      el.getAttribute('aria-label')?.trim() ||
      Array.from(content.querySelectorAll(`label[for="${el.id}"]`))
        .map((l) => l.textContent?.replace(/ /g, ' ').trim() || '')
        .find((t) => t.length > 0) ||
      el.id;
    if (!label || fields[label]) continue;
    fields[label] = {
      'Existing Value': el.checked ? 'true' : 'false',
      Description: 'true or false',
      Input: (val: string) => {
        const shouldCheck =
          val === 'true' || val === '1' || val?.toLowerCase() === 'yes';
        const liveEl =
          ((el.id
            ? document.getElementById(el.id)
            : null) as HTMLInputElement | null) ?? el;
        if (shouldCheck === liveEl.checked) return;
        const wasReadonly = liveEl.hasAttribute('readonly');
        if (wasReadonly) liveEl.removeAttribute('readonly');
        const lblEl = liveEl
          .closest('.w-checkbox')
          ?.querySelector('label.custom-control-label') as HTMLElement | null;
        if (lblEl) {
          lblEl.click();
        } else {
          liveEl.checked = shouldCheck;
          liveEl.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (wasReadonly) liveEl.setAttribute('readonly', '');
      }
    };
  }

  // Radio buttons — group by nearest anchor label
  const radioGroups = new Map<
    string,
    { input: HTMLInputElement; text: string }[]
  >();
  for (const w of allWidgets) {
    if (isNestedTabWidget(w)) continue;
    const el = w.querySelector(
      'input[type="radio"]'
    ) as HTMLInputElement | null;
    if (!el) continue;
    const groupLabel = nearestAnchor(getTop(w), getLeft(w));
    const text =
      content.querySelector(`label[for="${el.id}"]`)?.textContent?.trim() ||
      el.getAttribute('aria-label')?.trim() ||
      '';
    if (!text) continue;
    if (!radioGroups.has(groupLabel)) radioGroups.set(groupLabel, []);
    radioGroups.get(groupLabel)!.push({ input: el, text });
  }

  Array.from(radioGroups.entries()).forEach(([groupLabel, entries]) => {
    const choices = entries.map(
      (e: { input: HTMLInputElement; text: string }) => e.text
    );
    const selected = entries.find(
      (e: { input: HTMLInputElement; text: string }) => e.input.checked
    );
    fields[groupLabel] = {
      'Existing Value': selected?.text ?? null,
      Choices: choices,
      Input: (val: string) => {
        if (isEmptyOrNull(val)) return;
        const target = entries.find(
          (e: { input: HTMLInputElement; text: string }) => e.text === val
        );
        if (!target) return;
        const liveInput =
          ((target.input.id
            ? document.getElementById(target.input.id)
            : null) as HTMLInputElement | null) ?? target.input;
        const lblEl = liveInput
          .closest('.w-radio-button')
          ?.querySelector('label') as HTMLElement | null;
        const wasReadonly = liveInput.hasAttribute('readonly');
        if (wasReadonly) liveInput.removeAttribute('readonly');
        if (lblEl) {
          lblEl.click();
        } else {
          liveInput.checked = true;
          liveInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (wasReadonly) liveInput.setAttribute('readonly', '');
      }
    };
  });
}

async function buildPanelStructure(
  panel: Element,
  topLevelKey: string,
  nameOverride?: string
) {
  const subsectionName = nameOverride || panelCaptionName(panel);
  if (!subsectionName) return;
  if (/add\s+cc/i.test(subsectionName)) return;

  const structure = questionInteractions[topLevelKey].Structure;
  if (!structure[subsectionName]) structure[subsectionName] = {};
  const fields = structure[subsectionName];

  // For real panels use .w-panel-content; for virtual (nested tab) panels use the element itself
  // but exclude fields already inside a nested .w-panel (they're handled separately)
  // Use :scope > to only match a DIRECT .w-panel-content child (not nested panel contents).
  // When panel is a tab content element the direct child is not .w-panel-content, so we fall
  // back to the element itself and scan all direct fields (skipIfInPanel guards nested ones).
  const content = panel.querySelector(':scope > .w-panel-content') || panel;

  // ── Memo / rich-text fields ──────────────────────────────────────────────
  // When scanning virtual tab content, skip fields already inside a .w-panel (handled by phase 1)
  const skipIfInPanel = (el: Element) =>
    !!nameOverride && !!el.closest('.w-panel');

  content.querySelectorAll('.w-memo-content').forEach((memo) => {
    if (skipIfInPanel(memo)) return;
    const label = resolveLabel(memo, content, subsectionName);
    const el = memo as HTMLElement;
    bubbleFocus(el);
    fields[label] = {
      'Existing Value': el.innerText.trim() || null,
      Input: async (val: string, replyType: string) => {
        if (isEmptyOrNull(val)) return;
        const elId = el.id || label;
        if (replyType === 'append') {
          const trimmed = val.trim();
          if (!_memoAppendBase.has(elId))
            _memoAppendBase.set(elId, el.innerText?.trim() || '');
          const existing = _memoAppendedVals.get(elId) ?? [];
          if (existing.includes(trimmed)) return;
          existing.push(trimmed);
          _memoAppendedVals.set(elId, existing);
          const base = _memoAppendBase.get(elId)!;
          await eventBasedFill(
            el,
            base ? `${base}\n${existing.join('\n')}` : existing.join('\n')
          );
        } else {
          await eventBasedFill(el, val);
        }
      }
    };
  });

  // ── Date / text inputs ───────────────────────────────────────────────────
  content
    .querySelectorAll(
      '.w-field-openview input[type="text"], .w-field-openview input[inputmode="numeric"]'
    )
    .forEach((input) => {
      if (skipIfInPanel(input)) return;
      const el = input as HTMLInputElement;
      if (el.disabled) return;
      const isLinkedField = !!el.closest('.w-field-linked');
      const elName = el.getAttribute('name') || '';
      const label = resolveLabel(el, content, subsectionName);
      // Detect by placeholder (primary) or by name pattern as fallback (placeholder may be absent in live DOM)
      const isDateField = !!el.placeholder?.match(/mm[\/\-]dd/i);
      const isTimeOut = /^out\d*$/i.test(elName);
      const isTimeIn = /^in\d+$/i.test(elName);
      const isTimeField =
        !!el.placeholder?.toLowerCase().startsWith('hh:mm') ||
        isTimeOut ||
        isTimeIn;
      let fieldDescription: string | undefined;
      if (isDateField) {
        fieldDescription = 'Date in mm-dd-yy format (e.g. 06-01-26)';
      } else if (isTimeField) {
        fieldDescription = isTimeOut
          ? 'Time Out in hh:mma/p format (e.g. 10:00a or 1:30p). Must be later than Time In.'
          : 'Time In in hh:mma/p format (e.g. 8:00a or 10:00a). Must be earlier than Time Out.';
      } else if (isLinkedField) {
        fieldDescription =
          'Return the most specific ICD-10 code that corresponds to the patient underlying condition, symptoms, or functional limitations described in the transcript. ' +
          'Leave blank if there is insufficient information to determine a code.';
      } else if (/^init_frequency\d+$/i.test(elName)) {
        fieldDescription =
          'Number of visits per period (numeric only, e.g. 2). Do not include "x/week" or any text.';
      } else if (/^init_duration\d+$/i.test(elName)) {
        fieldDescription =
          'Number of weeks for this plan (numeric only, e.g. 8). Do not concatenate with the frequency number.';
      }
      fields[label] = {
        'Existing Value': el.value || null,
        Description: fieldDescription,
        Input: async (val: string) => {
          if (isEmptyOrNull(val)) return;
          // Linked fields (ICD codes) require a focus→fill→blur cycle so Raintree's
          // server validates and commits the value. Without focusin, the server
          // accepts the FieldSetContent message but returns an empty patch (no-op).
          if (isLinkedField) {
            bubbleFocus(el);
            el.focus();
            nativeInputFill(el, val);
            // Wait for Raintree's server-side ICD lookup to fire before committing.
            await new Promise((r) => setTimeout(r, 500));
            // Enter key tells Raintree to accept the first autocomplete match —
            // without this the value shows on screen but isn't committed to the record.
            el.dispatchEvent(
              new KeyboardEvent('keydown', {
                key: 'Enter',
                keyCode: 13,
                bubbles: true
              })
            );
            el.dispatchEvent(
              new KeyboardEvent('keypress', {
                key: 'Enter',
                keyCode: 13,
                bubbles: true
              })
            );
            el.dispatchEvent(
              new KeyboardEvent('keyup', {
                key: 'Enter',
                keyCode: 13,
                bubbles: true
              })
            );
            await new Promise((r) => setTimeout(r, 300));
            el.dispatchEvent(new FocusEvent('focusout', { bubbles: true }));
            el.blur();
            await new Promise((r) => setTimeout(r, 300));
            // Raintree sometimes navigates to a full-screen ICD10 Select picker after Enter.
            // Detect it and click back so we don't get stuck on that screen.
            const icd10Caption = document.querySelector(
              '.screen-header .screen-caption'
            );
            if (icd10Caption?.textContent?.trim() === 'ICD10 Select') {
              const backBtn = document.querySelector(
                '.backdown .backbtn, [id^="backbutton-"]'
              ) as HTMLElement | null;
              if (backBtn) {
                backBtn.click();
                await new Promise((r) => setTimeout(r, 500));
              }
            }
          } else {
            nativeInputFill(el, val);
          }
        }
      };
    });

  // ── Dropdown fields ──────────────────────────────────────────────────────
  content.querySelectorAll('.w-combobox-openview').forEach((sel) => {
    if (skipIfInPanel(sel)) return;
    const el = sel as HTMLSelectElement;
    if (el.disabled) return;
    const label = resolveLabel(el, content, subsectionName);
    const optionMap = new Map<string, string>(); // text → value attribute
    Array.from(el.options).forEach((o) => {
      const text = o.textContent?.trim() || '';
      if (text) optionMap.set(text, o.value);
    });
    const choices = Array.from(optionMap.keys());
    const existingText =
      el.selectedIndex >= 0
        ? el.options[el.selectedIndex]?.textContent?.trim() || el.value || null
        : el.value || null;
    fields[label] = {
      'Existing Value': existingText,
      Choices: choices,
      Input: (val: string) => {
        if (isEmptyOrNull(val)) return;
        // Match by display text first (LLM returns text), fall back to raw value
        const internalValue = optionMap.get(val) ?? val;
        bubbleFocus(el);
        el.focus();
        // Remove Raintree's readonly guard before changing value (same pattern as checkbox/radio)
        const wasReadonly = el.hasAttribute('readonly');
        if (wasReadonly) el.removeAttribute('readonly');
        const nativeSetter = Object.getOwnPropertyDescriptor(
          window.HTMLSelectElement.prototype,
          'value'
        )?.set;
        if (nativeSetter) {
          nativeSetter.call(el, internalValue);
        } else {
          el.value = internalValue;
        }
        el.dispatchEvent(new Event('input', { bubbles: true }));
        el.dispatchEvent(new Event('change', { bubbles: true }));
        const wrapper = el.closest('.w-combobox') as HTMLElement | null;
        if (wrapper)
          wrapper.dispatchEvent(new Event('change', { bubbles: true }));
        if (wasReadonly) el.setAttribute('readonly', '');
        el.blur();
      }
    };
  });

  // ── Checkboxes ───────────────────────────────────────────────────────────
  content
    .querySelectorAll('.w-checkbox input[type="checkbox"]')
    .forEach((checkbox) => {
      if (skipIfInPanel(checkbox)) return;
      // Skip checkboxes in tab-header widgets (negative top, e.g. "Show Unit Summary")
      const widgetEl = checkbox.closest(
        '[data-widgetname]'
      ) as HTMLElement | null;
      if (widgetEl && parseInt(widgetEl.style.top || '0') < 0) return;
      const el = checkbox as HTMLInputElement;
      const label = resolveLabel(el, content, subsectionName);
      fields[label] = {
        'Existing Value': el.checked ? 'true' : 'false',
        Description: 'true or false',
        Input: (val: string) => {
          const shouldCheck =
            val === 'true' || val === '1' || val?.toLowerCase() === 'yes';
          const liveEl =
            ((el.id
              ? document.getElementById(el.id)
              : null) as HTMLInputElement | null) ?? el;
          if (shouldCheck === liveEl.checked) return;
          const wasReadonly = liveEl.hasAttribute('readonly');
          if (wasReadonly) liveEl.removeAttribute('readonly');
          const lblEl = liveEl
            .closest('.w-checkbox')
            ?.querySelector('label.custom-control-label') as HTMLElement | null;
          if (lblEl) {
            lblEl.click();
          } else {
            liveEl.checked = shouldCheck;
            liveEl.dispatchEvent(new Event('change', { bubbles: true }));
          }
          if (wasReadonly) liveEl.setAttribute('readonly', '');
        }
      };
    });

  // ── Radio group(s) ───────────────────────────────────────────────────────
  const radios = Array.from(
    content.querySelectorAll('.w-radio-button input[type="radio"]')
  ).filter((r) => !skipIfInPanel(r)) as HTMLInputElement[];
  if (radios.length > 0) {
    // Check for standalone labels (w-label whose <label for=""> doesn't point to an input here).
    // When present, use position-based grouping so each label gets its own field.
    const standaloneAnchors: { text: string; top: number; left: number }[] = [];
    Array.from(content.querySelectorAll('[data-widgetname]')).forEach((w) => {
      if (skipIfInPanel(w)) return;
      const labelEl = w.querySelector('.w-label');
      if (!labelEl) return;
      const forId =
        (w.querySelector('label') as HTMLLabelElement | null)?.getAttribute(
          'for'
        ) || '';
      const pointsToInput =
        forId &&
        !!content.querySelector(
          `input#${CSS.escape(forId)}, select#${CSS.escape(forId)}`
        );
      if (!pointsToInput) {
        const text = labelEl.textContent?.trim() || '';
        const wEl = w as HTMLElement;
        if (text)
          standaloneAnchors.push({
            text,
            top: parseInt(wEl.style.top || '0'),
            left: parseInt(wEl.style.left || '0')
          });
      }
    });

    if (standaloneAnchors.length > 0) {
      // Group radios by nearest preceding standalone label using position scoring
      const radioGroupMap = new Map<
        string,
        { input: HTMLInputElement; text: string }[]
      >();
      radios.forEach((r) => {
        const rWidget = r.closest('[data-widgetname]') as HTMLElement | null;
        const rTop = rWidget ? parseInt(rWidget.style.top || '0') : 0;
        const rLeft = rWidget ? parseInt(rWidget.style.left || '0') : 0;
        let best = '';
        let bestScore = Infinity;
        for (const a of standaloneAnchors) {
          if (a.top > rTop) continue;
          const score = rTop - a.top + Math.abs(a.left - rLeft);
          if (score < bestScore) {
            bestScore = score;
            best = a.text;
          }
        }
        const groupLabel = best.replace(/[:\s]+$/, '') || subsectionName;
        const rText =
          content.querySelector(`label[for="${r.id}"]`)?.textContent?.trim() ||
          r.getAttribute('aria-label')?.trim() ||
          '';
        if (!rText) return;
        if (!radioGroupMap.has(groupLabel)) radioGroupMap.set(groupLabel, []);
        radioGroupMap.get(groupLabel)!.push({ input: r, text: rText });
      });
      Array.from(radioGroupMap.entries()).forEach(([groupLabel, entries]) => {
        const choices = entries.map(
          (e: { input: HTMLInputElement; text: string }) => e.text
        );
        const selected = entries.find(
          (e: { input: HTMLInputElement; text: string }) => e.input.checked
        );
        fields[groupLabel] = {
          'Existing Value': selected?.text ?? null,
          Choices: choices,
          Input: (val: string) => {
            if (isEmptyOrNull(val)) return;
            const target = entries.find(
              (e: { input: HTMLInputElement; text: string }) =>
                e.text === val || matchesChoice(val, e.text)
            );
            if (!target) return;
            const liveRadio = (
              target.input.id ? document.getElementById(target.input.id) : null
            ) as HTMLInputElement | null;
            const liveLabel = target.input.id
              ? (document.querySelector(
                  `label[for="${target.input.id}"]`
                ) as HTMLElement | null)
              : null;
            if (liveLabel && liveRadio) {
              const wasReadonly = liveRadio.hasAttribute('readonly');
              if (wasReadonly) liveRadio.removeAttribute('readonly');
              liveLabel.click();
              if (wasReadonly) liveRadio.setAttribute('readonly', '');
            } else if (liveRadio) {
              liveRadio.removeAttribute('readonly');
              liveRadio.checked = true;
              liveRadio.dispatchEvent(new Event('change', { bubbles: true }));
              liveRadio.setAttribute('readonly', '');
            } else {
              console.warn(
                `[Raintree] Radio not found in live DOM: ${target.input.id}`
              );
            }
          }
        };
      });
    } else {
      buildRadioScaleField(radios, content, fields);
    }
  }

  // ── Picker widgets (multi-select grid with Quick find) ───────────────────
  content.querySelectorAll('.w-picker').forEach((picker) => {
    if (skipIfInPanel(picker)) return;
    buildPickerField(picker, content, subsectionName, fields);
  });

  // ── Listboxes with checkbox rows ─────────────────────────────────────────
  for (const listbox of Array.from(content.querySelectorAll('.w-listbox'))) {
    if (skipIfInPanel(listbox)) continue;
    if (listbox.querySelector('.w-listbox-scroller.disabled')) continue;
    // Try checkbox-row listbox first; then row-list (Goals/Chief Complaints style); then add-row
    if (!buildCheckboxListboxField(listbox, content, subsectionName, fields)) {
      if (
        !(await buildRowListField(listbox, content, subsectionName, fields))
      ) {
        await buildAddRowListboxField(listbox, content, subsectionName, fields);
      }
    }
  }
}

// ─── Field Builders ───────────────────────────────────────────────────────────

function buildRadioScaleField(
  radios: HTMLInputElement[],
  container: Element,
  fields: Record<string, any>
) {
  // Collect label text for each radio from aria-label or label[for=id]
  const radioValueMap = new Map<HTMLInputElement, string>();
  radios.forEach((r) => {
    const fromLabel = container
      .querySelector(`label[for="${r.id}"]`)
      ?.textContent?.trim();
    const fromAria = r.getAttribute('aria-label')?.trim();
    const text = fromLabel || fromAria || '';
    if (text) radioValueMap.set(r, text);
  });
  // Fill gaps with index for any unmapped radios
  radios.forEach((r, i) => {
    if (!radioValueMap.has(r)) radioValueMap.set(r, String(i));
  });

  const choices = Array.from(radioValueMap.values());
  // Numeric pain scale if all choices are numeric
  const allNumeric = choices.every((c) => /^\d+$/.test(c));
  const sorted = allNumeric ? choices.slice().sort((a, b) => +a - +b) : choices;
  const fieldLabel = allNumeric
    ? `Pain Scale (${sorted[0]}-${sorted[sorted.length - 1]})`
    : 'Value';
  const description = allNumeric
    ? `${sorted[0]} = No Pain, ${sorted[sorted.length - 1]} = Worst Possible Pain`
    : `Choose one of: ${sorted.join(', ')}`;
  const selected = radios.find((r) => r.checked);

  fields[fieldLabel] = {
    'Existing Value': selected ? radioValueMap.get(selected) || null : null,
    Choices: sorted,
    Description: description,
    Input: (val: string) => {
      if (isEmptyOrNull(val)) return;
      Array.from(radioValueMap.entries()).some(([radio, value]) => {
        if (value !== val) return false;
        const lblEl = container.querySelector(
          `label[for="${radio.id}"]`
        ) as HTMLElement;
        if (lblEl) {
          lblEl.click();
        } else {
          bubbleFocus(radio);
          radio.removeAttribute('readonly');
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
          radio.setAttribute('readonly', '');
        }
        return true;
      });
    }
  };
}

function buildPickerField(
  picker: Element,
  container: Element,
  subsectionName: string,
  fields: Record<string, any>
) {
  const items = Array.from(picker.querySelectorAll('.w-picker-item'));
  if (items.length === 0) return;

  const all = items
    .map((item) => item.querySelector('.text')?.textContent?.trim() || '')
    .filter(Boolean);

  const existing = items
    .filter(
      (item) =>
        (item.querySelector('input[type="checkbox"]') as HTMLInputElement)
          ?.checked
    )
    .map((item) => item.querySelector('.text')?.textContent?.trim() || '')
    .filter(Boolean);

  const label = resolveLabel(picker, container, subsectionName);

  fields[label] = {
    'Existing Value': existing.length > 0 ? existing.join(', ') : null,
    Description: `IMPORTANT: You MUST respond using ONLY exact values from the list below — do NOT paraphrase or invent new values. Separate multiple selections with '$'. If nothing applies, reply with exactly: None\nAvailable choices: ${all.join(' | ')}`,
    Input: (val: string | string[]) => {
      if (isEmptyOrNull(val as string)) return;
      const targets = Array.isArray(val)
        ? val
        : (val as string)
            .split('$')
            .map((v) => v.trim())
            .filter(Boolean);
      items.forEach((item) => {
        const text = item.querySelector('.text')?.textContent?.trim() || '';
        const checkbox = item.querySelector(
          'input[type="checkbox"]'
        ) as HTMLInputElement | null;
        if (
          targets.some((t) => t.toLowerCase() === text.toLowerCase()) &&
          !checkbox?.checked
        ) {
          (item as HTMLElement).click();
        }
      });
    }
  };
}

/**
 * Returns true if `target` matches `choiceText`.
 * Supports @@ as a wildcard for template choices (e.g. "Pain @@ out of 10").
 */
function matchesChoice(target: string, choiceText: string): boolean {
  // Normalize: lowercase, strip apostrophes/smart quotes, collapse whitespace
  const normalize = (s: string) =>
    s
      .toLowerCase()
      .replace(/['''\u2018\u2019`]/g, '')
      .replace(/["""\u201C\u201D]/g, '"')
      .replace(/\s+/g, ' ')
      .trim();
  const buildWildcardRegex = (template: string) => {
    const escaped = template
      .replace(/<@@>/g, '\x00') // angle-bracket-wrapped marker first
      .replace(/@@/g, '\x00') // bare marker
      .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      .replace(/\x00/g, '.+');
    return new RegExp('^' + escaped + '$', 'i');
  };
  if (target.toLowerCase() === choiceText.toLowerCase()) return true;
  if (normalize(target) === normalize(choiceText)) return true;
  if (choiceText.includes('@@')) {
    if (buildWildcardRegex(choiceText).test(target)) return true;
    if (buildWildcardRegex(normalize(choiceText)).test(normalize(target)))
      return true;
  }
  return false;
}

function buildCheckboxListboxField(
  listbox: Element,
  container: Element,
  subsectionName: string,
  fields: Record<string, any>
): boolean {
  const rows = Array.from(listbox.querySelectorAll('tbody tr'));
  // Ignore extended-listbox row-selector checkboxes (.w-listbox-extended-checkbox);
  // only count checkboxes that are actual data fields.
  const hasCheckboxRows = rows.some((r) => {
    const chk = r.querySelector('input[type="checkbox"]');
    return chk && !chk.closest('.w-listbox-extended-checkbox');
  });
  if (!hasCheckboxRows) return false;

  const existing: string[] = [];
  const all: string[] = [];

  rows.forEach((row) => {
    const checkbox = row.querySelector(
      'input[type="checkbox"]'
    ) as HTMLInputElement | null;
    const cells = row.querySelectorAll('td');
    // Extended listboxes: td[0] is checkbox cell, td[1] is text
    const textCell = checkbox ? cells[1] : cells[0];
    const text = textCell?.textContent?.trim() || '';
    if (!text) return;
    all.push(text);
    if (checkbox?.checked) existing.push(text);
  });

  if (all.length === 0) return false;

  // Use the label resolving logic: find a label pointing to the listbox id,
  // or fall back to subsection name (panel caption)
  const label = resolveLabel(listbox, container, subsectionName);

  fields[label] = {
    'Existing Value': existing.length > 0 ? existing.join(', ') : null,
    Description: `Select all applicable from: ${all.join(', ')}. Provide '$' separated values.`,
    Input: (val: string | string[]) => {
      const targets = Array.isArray(val)
        ? val
        : val
            .split('$')
            .map((v) => v.trim())
            .filter(Boolean);
      rows.forEach((row) => {
        const checkbox = row.querySelector(
          'input[type="checkbox"]'
        ) as HTMLInputElement | null;
        const cells = row.querySelectorAll('td');
        const textCell = checkbox ? cells[1] : cells[0];
        const text = textCell?.textContent?.trim() || '';
        if (
          targets.some((t) => t.toLowerCase() === text.toLowerCase()) &&
          !checkbox?.checked
        ) {
          (row as HTMLElement).click();
        }
      });
    }
  };
  return true;
}

async function buildAddRowListboxField(
  listbox: Element,
  container: Element,
  subsectionName: string,
  fields: Record<string, any>
) {
  const addBtn = listbox.querySelector(
    '.w-listbox-toolbar-button.mdi-plus-box'
  ) as HTMLElement | null;
  if (!addBtn) return; // not an add-row listbox

  const label = resolveLabel(listbox, container, subsectionName);

  // ── During GET_DOM scan only: open picker to read available choices, then close ──
  // Skip during fill (answeringInProgress=true) to avoid double-opening the picker.
  let pickerChoices: string[] = [];
  if (!answeringInProgress) {
    const pickersBefore = new Set(
      Array.from(document.querySelectorAll('.w-picker'))
    );
    addBtn.click();
    let waited = 0;
    let picker: Element | null = null;
    while (waited < 2000) {
      for (const p of Array.from(document.querySelectorAll('.w-picker'))) {
        if (
          !pickersBefore.has(p) &&
          p.querySelectorAll('.w-picker-item').length > 0
        ) {
          picker = p;
          break;
        }
      }
      if (picker) break;
      await new Promise((r) => setTimeout(r, 100));
      waited += 100;
    }
    if (picker) {
      pickerChoices = Array.from(
        picker.querySelectorAll('.w-picker-item .text')
      )
        .map((el) => el.textContent?.trim() || '')
        .filter(Boolean);
      // Full-screen navigation — use back button to dismiss without selecting
      const backBtn = document.querySelector(
        '.backbtn, [id^="backbutton-"]'
      ) as HTMLElement | null;
      if (backBtn) {
        backBtn.click();
      } else {
        document.dispatchEvent(
          new KeyboardEvent('keydown', {
            key: 'Escape',
            bubbles: true,
            cancelable: true
          })
        );
      }
      await new Promise((r) => setTimeout(r, 500));
    } else {
      console.warn(
        `[Raintree] buildAddRowListboxField: picker did not open for "${label}"`
      );
    }
  }

  const existingRows = Array.from(listbox.querySelectorAll('tbody tr'))
    .map((row) =>
      Array.from(row.querySelectorAll('td'))
        .map((td) => td.textContent?.trim() || '')
        .filter(Boolean)
        .join(' | ')
    )
    .filter(Boolean);

  const batchKey = `${subsectionName}.${label}`;
  // Capture the listbox ID now so the flush can find it after DOM remounts
  const listboxId = listbox.getAttribute('id') || '';

  // Register the flush function for this field (overwrites on each rebuild with fresh element refs)
  _pickerBatchFlushers.set(batchKey, async () => {
    const targets = _pickerBatch.get(batchKey) ?? [];
    _pickerBatch.delete(batchKey);
    if (targets.length === 0) return;

    // Re-query the listbox — first try by ID, then fall back to panel scan
    let freshListbox: Element | null = listboxId
      ? document.getElementById(listboxId)
      : null;
    if (!freshListbox) {
      // Listbox not in DOM — its sub-tab may not be active. Activate it by aria-label or span text.
      const tabItem = (
        Array.from(
          document.querySelectorAll('.w-tab-control-tabs .tab-item')
        ) as HTMLElement[]
      ).find(
        (t) =>
          (t.getAttribute('aria-label')?.trim() ||
            t.querySelector('span')?.textContent?.trim()) === subsectionName
      );
      if (tabItem && !tabItem.classList.contains('active')) {
        tabItem.click();
        await new Promise((r) => setTimeout(r, 400));
      }
      freshListbox = listboxId ? document.getElementById(listboxId) : null;
    }
    if (!freshListbox) {
      // Last resort: search panels
      for (const panel of Array.from(document.querySelectorAll('.w-panel'))) {
        if (panelCaptionName(panel) !== subsectionName) continue;
        freshListbox = panel.querySelector('.w-listbox');
        break;
      }
    }
    let freshBtn = freshListbox?.querySelector(
      '.w-listbox-toolbar-button.mdi-plus-box'
    ) as HTMLElement | null;
    if (!freshBtn) {
      console.warn(
        `[Raintree] pickerFlush: addBtn not found for "${batchKey}"`
      );
      return;
    }

    // Open picker
    const pickersBefore = new Set(
      Array.from(document.querySelectorAll('.w-picker'))
    );
    freshBtn.click();
    let waited = 0;
    let picker: Element | null = null;
    while (waited < 2000) {
      for (const p of Array.from(document.querySelectorAll('.w-picker'))) {
        if (
          !pickersBefore.has(p) &&
          p.querySelectorAll('.w-picker-item').length > 0
        ) {
          picker = p;
          break;
        }
      }
      if (picker) break;
      await new Promise((r) => setTimeout(r, 100));
      waited += 100;
    }
    if (!picker) {
      console.warn(
        `[Raintree] pickerFlush: picker did not open for "${batchKey}"`
      );
      return;
    }

    // Select all queued targets in one pass
    const items = Array.from(picker.querySelectorAll('.w-picker-item'));
    let matched = 0;
    for (const item of items) {
      const text = item.querySelector('.text')?.textContent?.trim() || '';
      const checkbox = item.querySelector(
        'input[type="checkbox"]'
      ) as HTMLInputElement | null;
      if (targets.some((t) => matchesChoice(t, text)) && !checkbox?.checked) {
        checkbox?.click();
        matched++;
        await new Promise((r) => setTimeout(r, 80));
      }
    }

    // Click the Save button in the picker toolbar
    const saveBtn = document.querySelector(
      '.w-picker-toolbar .mdi-content-save'
    ) as HTMLElement | null;
    if (saveBtn) {
      saveBtn.click();
    } else {
      console.warn(
        '[Raintree] pickerFlush: save button not found, falling back to back button'
      );
      const backBtn = (document.querySelector('.backdown') ??
        document.querySelector('.backbtn')) as HTMLElement | null;
      backBtn?.click();
    }
    await new Promise((r) => setTimeout(r, 800));
  });

  fields[label] = {
    'Existing Value': existingRows.length > 0 ? existingRows.join(', ') : null,
    Description:
      pickerChoices.length > 0
        ? `IMPORTANT: You MUST respond using ONLY exact values from the list below — do NOT paraphrase or invent new values. ` +
          `Only select items that were ACTUALLY performed or discussed in the session. ` +
          `Do NOT select an item merely because it sounds similar — if no choice precisely matches what was done, omit it rather than picking incorrectly. ` +
          `Values containing "@@ " are templates — use them EXACTLY as shown, keeping "@@ " as-is (do NOT replace @@ with actual text). ` +
          `Separate multiple selections with '$'. If nothing applies, reply with exactly: None\nAvailable choices: ${pickerChoices.join(' , ')}`
        : `Add rows by providing comma-separated values.`,
    Input: (val: string | string[]) => {
      if (isEmptyOrNull(val)) return;
      const newTargets = Array.isArray(val)
        ? val
        : String(val)
            .split('$')
            .map((v) => v.trim())
            .filter(Boolean);
      if (!_pickerBatch.has(batchKey)) _pickerBatch.set(batchKey, []);
      _pickerBatch.get(batchKey)!.push(...newTargets);
    }
  };
}

/**
 * Handles row-list listboxes (Goals, Chief Complaints) that have an Edit (mdi-pen) toolbar button.
 * Each row is a form with multiple columns; new rows are added via a picker.
 * Returns true if this listbox was handled as a row-list.
 */
/**
 * Handles row-list listboxes (Goals, Chief Complaints) that have an Edit (mdi-pen) toolbar button.
 * Structure mirrors WebPT1 goals: Existing Value is an array of row objects, each with named column
 * fields. The AI responds with an array of the same shape; each element is processed row by row.
 * Returns true if this listbox was handled as a row-list.
 */
async function buildRowListField(
  listbox: Element,
  container: Element,
  subsectionName: string,
  fields: Record<string, any>
): Promise<boolean> {
  if (!listbox.querySelector('.w-listbox-toolbar-button.mdi-pen')) return false;

  const addBtn = listbox.querySelector(
    '.w-listbox-toolbar-button.mdi-plus-box'
  ) as HTMLElement | null;

  const label = resolveLabel(listbox, container, subsectionName);

  // Column headers — skip checkbox and toolbar header cells
  const headers = Array.from(listbox.querySelectorAll('thead th'))
    .filter(
      (th) =>
        !th.classList.contains('w-listbox-extended-checkbox') &&
        !th.classList.contains('w-listbox-toolbar-header')
    )
    .map((th) => th.textContent?.trim() || '')
    .filter(Boolean);

  // Existing rows as array of { columnName: value } objects — same shape as the AI answer
  const existingRowData: Record<string, string>[] = Array.from(
    listbox.querySelectorAll('tbody tr')
  )
    .map((row) => {
      const cells = Array.from(row.querySelectorAll('td')).filter(
        (td) =>
          !td.classList.contains('w-listbox-extended-checkbox') &&
          !td.classList.contains('w-listbox-toolbar-cell')
      );
      const obj: Record<string, string> = {};
      headers.forEach((h, i) => {
        obj[h] = cells[i]?.textContent?.trim() || '';
      });
      return obj;
    })
    .filter((obj) => Object.values(obj).some((v) => v));

  // During GET_DOM scan: open picker to read available choices, then close
  let pickerChoices: string[] = [];
  if (!answeringInProgress && addBtn) {
    const pickersBefore = new Set(
      Array.from(document.querySelectorAll('.w-picker'))
    );
    addBtn.click();
    let waited = 0;
    let picker: Element | null = null;
    while (waited < 2000) {
      for (const p of Array.from(document.querySelectorAll('.w-picker'))) {
        if (
          !pickersBefore.has(p) &&
          p.querySelectorAll('.w-picker-item').length > 0
        ) {
          picker = p;
          break;
        }
      }
      if (picker) break;
      await new Promise((r) => setTimeout(r, 100));
      waited += 100;
    }
    if (picker) {
      pickerChoices = Array.from(
        picker.querySelectorAll('.w-picker-item .text')
      )
        .map((el) => el.textContent?.trim() || '')
        .filter(Boolean);
      const backBtn = document.querySelector(
        '.backbtn, [id^="backbutton-"]'
      ) as HTMLElement | null;
      if (backBtn) {
        backBtn.click();
      } else {
        document.dispatchEvent(
          new KeyboardEvent('keydown', {
            key: 'Escape',
            bubbles: true,
            cancelable: true
          })
        );
      }
      await new Promise((r) => setTimeout(r, 500));
    } else {
      console.warn(
        `[Raintree] buildRowListField: picker did not open for "${label}"`
      );
    }
  }

  const listboxId = listbox.getAttribute('id') || '';
  const batchKey = `${subsectionName}.${label}`;

  // Flush: process each queued row object in order.
  // The fill phase may have navigated to a different sub-tab, leaving this listbox
  // out of the DOM. Re-activate the sub-tab by its label before filling.
  _pickerBatchFlushers.set(batchKey, async () => {
    const targets = (_pickerBatch.get(batchKey) ?? []) as Record<
      string,
      string
    >[];
    _pickerBatch.delete(batchKey);
    if (targets.length === 0) return;

    if (listboxId && !document.getElementById(listboxId)) {
      const tabItem = (
        Array.from(
          document.querySelectorAll('.w-tab-control-tabs .tab-item')
        ) as HTMLElement[]
      ).find(
        (t) =>
          (t.getAttribute('aria-label')?.trim() ||
            t.querySelector('span')?.textContent?.trim()) === subsectionName
      );
      if (tabItem && !tabItem.classList.contains('active')) {
        tabItem.click();
        await new Promise((r) => setTimeout(r, 500));
      }
    }

    for (const rowObj of targets) {
      await fillRowListRow(listboxId, rowObj, headers);
    }
  });

  // Build the list-style Comprehend structure the backend expects.
  // Each element is a row object where every column is a field using ONLY the valid backend keys:
  //   "Existing Value", "Description", "Choices", "Type"
  // Choices are attached ONLY to the first row (prototype). Cap at 499 to stay within OpenAI's
  // 500-item enum limit (backend appends one sentinel, making the total exactly 500).
  const MAX_PICKER_CHOICES = 499;
  const cappedPickerChoices = pickerChoices.slice(0, MAX_PICKER_CHOICES);
  const isGoals = label.toLowerCase().includes('goal');
  const buildRowEntry = (
    row: Record<string, string>,
    includeChoices: boolean
  ): Record<string, any> => {
    const obj: Record<string, any> = {};
    headers.forEach((h, i) => {
      const fieldDef: Record<string, any> = {
        _existingValue: row[h] || null
      };
      if (i === 0 && includeChoices && cappedPickerChoices.length > 0) {
        fieldDef['_choices'] = cappedPickerChoices;
        fieldDef['_description'] = isGoals
          ? 'Select from the listed Choices exactly as written, keeping @@ and <@@> placeholders as-is — ' +
            'do NOT replace them here. Provide the resolved version separately in "Goal Name". ' +
            'You may also provide a fully custom goal text (not from the list) if no choice matches. ' +
            'Do not duplicate an existing row.'
          : 'Value MUST be one of the listed Choices exactly as written. ' +
            'Only select items that were ACTUALLY performed or discussed in this session — ' +
            'do NOT select an item merely because it sounds similar to something else. ' +
            'The name you select must precisely match the activity performed. ' +
            'If no choice is an accurate match, omit it rather than picking incorrectly. ' +
            'Do not duplicate an existing row.';
      }
      // Add column-specific choices/descriptions for goal columns
      if (isGoals) {
        const hNorm = h.toLowerCase().replace(/[\s\-\/]/g, '');
        if (hNorm === 'shortlong' || hNorm === 'shortterm') {
          fieldDef['_choices'] = ['Long Term', 'Short Term'];
        } else if (hNorm === 'result') {
          fieldDef['_choices'] = [
            'Discontinued',
            'Met',
            'New',
            'Not Met',
            'Ongoing',
            'Partially met'
          ];
        } else if (hNorm === 'timeframe') {
          fieldDef['_description'] =
            'Treatment timeframe, e.g., "2 weeks", "4 weeks", "1 month", "10+ weeks"';
        }
      }
      obj[h] = fieldDef;
    });
    // For goals listboxes: add "Goal Name" so the AI can provide a resolved version
    // (with @@ replaced) that gets typed into the edit form's Goal Name field.
    if (isGoals) {
      const gnDef: Record<string, any> = { _existingValue: null };
      if (includeChoices) {
        gnDef['_description'] =
          'If the Goals entry contains @@ or <@@> placeholders, provide the COMPLETE goal text ' +
          'with every placeholder replaced by a specific value from the transcript ' +
          '(e.g., "walk <@@> minutes" → "walk 20 minutes", "carry @@ lbs" → "carry 20 lbs"). ' +
          'Omit this field entirely if there are no placeholders.';
      }
      obj['Goal Name'] = gnDef;
    }
    return obj;
  };

  // Goals listboxes: send existing rows (so the AI can update comment/result) followed
  // by a prototype row (so the AI can also suggest new goals).
  // Existing rows carry _existingValue for each column and a description telling the AI
  // to only update comment/result — NOT to re-add them as new entries.
  // The prototype (with _existingValue: null) signals "add new goals here".
  // Non-goals listboxes keep the original behaviour (send all existing rows).
  let rowStructure: Record<string, any>[];
  if (isGoals) {
    const prototype = buildRowEntry(
      Object.fromEntries(headers.map((h) => [h, ''])),
      true
    );
    if (existingRowData.length > 0) {
      const existingRows = existingRowData.map((row) => {
        const entry = buildRowEntry(row, false);
        const primaryField = entry[headers[0]];
        if (primaryField && typeof primaryField === 'object') {
          primaryField['_description'] =
            'EXISTING goal — do NOT add as a new entry. ' +
            'Only update the comment field (and result/timeframe if clearly stated in the transcript). ' +
            'Leave all other fields unchanged.';
        }
        return entry;
      });
      rowStructure = [...existingRows, prototype];
    } else {
      rowStructure = [prototype];
    }
  } else {
    // Non-goals: send all existing rows so the AI can update/extend them
    rowStructure =
      existingRowData.length > 0
        ? existingRowData.map((row, idx) => buildRowEntry(row, idx === 0))
        : [
            buildRowEntry(Object.fromEntries(headers.map((h) => [h, ''])), true)
          ];
  }

  fields[label] = {
    // flex++ list: backend expand_flexpp_lists_recursively handles array sizing
    Structure: rowStructure,
    // Input receives one row object at a time — answerFlexQuestion iterates the AI's array
    Input: (val: Record<string, string> | string) => {
      if (isEmptyOrNull(val as string)) return;
      let rowObj: Record<string, string>;
      if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
        rowObj = val as Record<string, string>;
      } else {
        const parts = String(val)
          .split('|')
          .map((v) => v.trim());
        rowObj = {};
        headers.forEach((h, i) => {
          rowObj[h] = parts[i] || '';
        });
      }
      if (!_pickerBatch.has(batchKey)) _pickerBatch.set(batchKey, []);
      _pickerBatch.get(batchKey)!.push(rowObj);
    }
  };
  return true;
}

/** Find a row in a listbox by matching the primary (first non-checkbox) cell text. */
function findRowInListbox(
  listbox: Element,
  primaryValue: string
): Element | null {
  const pvLower = primaryValue.toLowerCase();
  // First pass: exact match or matchesChoice (handles wildcards / smart quotes)
  for (const row of Array.from(listbox.querySelectorAll('tbody tr'))) {
    const cells = Array.from(row.querySelectorAll('td')).filter(
      (td) => !td.classList.contains('w-listbox-extended-checkbox')
    );
    const cellText = cells[0]?.textContent?.trim() || '';
    if (matchesChoice(primaryValue, cellText)) return row;
  }
  // Second pass: listbox may truncate long text — accept if cell text is a
  // prefix of primaryValue (at least 20 chars) or primaryValue starts with cell text.
  for (const row of Array.from(listbox.querySelectorAll('tbody tr'))) {
    const cells = Array.from(row.querySelectorAll('td')).filter(
      (td) => !td.classList.contains('w-listbox-extended-checkbox')
    );
    const cellText = (cells[0]?.textContent?.trim() || '').toLowerCase();
    if (cellText.length >= 20 && pvLower.startsWith(cellText)) return row;
    if (cellText.length >= 20 && cellText.startsWith(pvLower)) return row;
  }
  return null;
}

/**
 * Add or edit one row in a row-list listbox.
 * rowObj keys are column header names; first column is the primary/picker value.
 * Column 0 is auto-filled by the picker; columns 1+ are filled in the edit form.
 */
async function fillRowListRow(
  listboxId: string,
  rowObj: Record<string, string>,
  headers: string[]
) {
  const primaryKey = headers[0];
  const primaryValue = rowObj[primaryKey]?.trim();
  if (!primaryValue) return;

  // Track any TinyMCE memo field filled in this row so we can flush the diff
  // sync (and wait for server ACK) before clicking OK — Raintree commits memo
  // content to its store via a periodic sync, not on OK.
  let filledMemoEl: HTMLElement | null = null;
  let filledMemoXHTML: string | null = null;

  let freshListbox: Element | null = listboxId
    ? document.getElementById(listboxId)
    : null;
  if (!freshListbox) {
    console.warn(
      `[Raintree] fillRowListRow: listbox not found id="${listboxId}"`
    );
    return;
  }

  // Skip duplicate — row already exists with this primary value
  const existingRow = findRowInListbox(freshListbox, primaryValue);
  let targetRow: Element | null = existingRow;

  if (!existingRow) {
    // Add via picker
    const addBtn = freshListbox.querySelector(
      '.w-listbox-toolbar-button.mdi-plus-box'
    ) as HTMLElement | null;
    if (!addBtn) {
      console.warn(
        `[Raintree] fillRowListRow: addBtn not found for "${primaryValue}"`
      );
      return;
    }

    const pickersBefore = new Set(
      Array.from(document.querySelectorAll('.w-picker'))
    );
    addBtn.click();
    let waited = 0;
    let picker: Element | null = null;
    while (waited < 2000) {
      for (const p of Array.from(document.querySelectorAll('.w-picker'))) {
        if (
          !pickersBefore.has(p) &&
          p.querySelectorAll('.w-picker-item').length > 0
        ) {
          picker = p;
          break;
        }
      }
      if (picker) break;
      await new Promise((r) => setTimeout(r, 100));
      waited += 100;
    }
    if (!picker) {
      console.warn(
        `[Raintree] fillRowListRow: picker did not open for "${primaryValue}"`
      );
      return;
    }

    // Find and select the matching picker item (@@-aware)
    const items = Array.from(picker.querySelectorAll('.w-picker-item'));
    let matched = false;
    for (const item of items) {
      const text = item.querySelector('.text')?.textContent?.trim() || '';
      if (matchesChoice(primaryValue, text)) {
        const cb = item.querySelector(
          'input[type="checkbox"]'
        ) as HTMLInputElement | null;
        if (cb && !cb.checked) {
          cb.click();
          await new Promise((r) => setTimeout(r, 80));
        }
        matched = true;
        break;
      }
    }
    if (!matched) {
      console.warn(
        `[Raintree] fillRowListRow: no picker match for "${primaryValue}"`
      );
      const backBtn = document.querySelector(
        '.backbtn, [id^="backbutton-"]'
      ) as HTMLElement | null;
      backBtn?.click();
      await new Promise((r) => setTimeout(r, 300));
      return;
    }

    // Save picker selection
    const saveBtn = document.querySelector(
      '.w-picker-toolbar .mdi-content-save'
    ) as HTMLElement | null;
    if (saveBtn) {
      saveBtn.click();
    } else {
      const backBtn = (document.querySelector('.backdown') ??
        document.querySelector('.backbtn')) as HTMLElement | null;
      backBtn?.click();
    }
    // Poll for the newly added row — Raintree's React reconciler may take
    // longer than a single fixed wait, especially for the last row in a list.
    let rowWaited = 0;
    while (rowWaited <= 2000) {
      freshListbox = listboxId
        ? document.getElementById(listboxId)
        : freshListbox;
      if (freshListbox) {
        targetRow = findRowInListbox(freshListbox, primaryValue);
        if (targetRow) break;
      }
      await new Promise((r) => setTimeout(r, 200));
      rowWaited += 200;
    }
    if (!targetRow) {
      console.warn(
        `[Raintree] fillRowListRow: newly added row not found for "${primaryValue}"`
      );
      return;
    }
  }

  // "Goal Name" carries the @@ -resolved goal text for goals with placeholders.
  const goalNameValue =
    (rowObj['Goal Name'] as string | undefined)?.trim() || '';

  // Nothing extra to fill in the edit form — done
  const hasExtraData =
    !!goalNameValue || headers.slice(1).some((h) => rowObj[h]?.trim());
  if (!hasExtraData) return;

  // Right-click the first data cell to open Raintree's context menu, then click Edit.
  const firstDataCell = Array.from(targetRow.querySelectorAll('td')).find(
    (td) =>
      !td.classList.contains('w-listbox-extended-checkbox') &&
      !td.classList.contains('w-listbox-toolbar-cell')
  ) as HTMLElement | null;
  const clickTarget = firstDataCell ?? (targetRow as HTMLElement);
  clickTarget.dispatchEvent(
    new MouseEvent('contextmenu', {
      bubbles: true,
      cancelable: true,
      button: 2
    })
  );

  // Wait for the context menu, then click the Edit item. Raintree renders
  // its context menu as `.popup-overlay > .popup` with `.popup-item[aria-label]`
  // children — NOT `.w-context-menu`. Match on aria-label so we pick the right
  // action regardless of the menu's text formatting (which uses `<u>` for the
  // keyboard-shortcut letter).
  let contextMenu: Element | null = null;
  for (let attempt = 0; attempt < 20; attempt++) {
    await new Promise((r) => setTimeout(r, 100));
    contextMenu = document.querySelector('.popup-overlay') ?? null;
    if (contextMenu) break;
  }
  if (contextMenu) {
    const editItem = contextMenu.querySelector(
      '.popup-item[aria-label="Edit"]'
    ) as HTMLElement | null;
    if (editItem) {
      editItem.click();
    } else {
      console.warn(
        '[Raintree] fillRowListRow: Edit item not found in context menu'
      );
      return;
    }
  } else {
    // Fallback: click row to focus then use toolbar pen button
    console.warn(
      '[Raintree] fillRowListRow: context menu did not open, trying toolbar'
    );
    clickTarget.click();
    await new Promise((r) => setTimeout(r, 200));
    const freshListbox2 = listboxId
      ? document.getElementById(listboxId)
      : freshListbox;
    const freshEditBtn = freshListbox2?.querySelector(
      '.w-listbox-toolbar-button.mdi-pen'
    ) as HTMLElement | null;
    if (!freshEditBtn) {
      console.warn(
        `[Raintree] fillRowListRow: editBtn not found after row select`
      );
      return;
    }
    freshEditBtn.click();
  }

  // Poll for the edit form container — up to 2 s in 100 ms increments
  let formContainer: Element | null = null;
  for (let attempt = 0; attempt < 20; attempt++) {
    await new Promise((r) => setTimeout(r, 100));
    formContainer =
      document
        .querySelector('[data-widgetname="btnok"]')
        ?.closest('.screen-inner') ?? null;
    if (formContainer) break;
  }

  // Fill columns 1+ in the edit form by matching label[for] text to header names.
  // Scope to formContainer so we don't match stale labels from other parts of the page.
  const labelRoot = formContainer ?? document;
  for (let i = 1; i < headers.length; i++) {
    const colValue = rowObj[headers[i]]?.trim();
    if (!colValue) continue;

    const lbl = Array.from(labelRoot.querySelectorAll('label[for]')).find(
      (l) => l.textContent?.trim().toLowerCase() === headers[i].toLowerCase()
    ) as HTMLLabelElement | null;
    if (!lbl) {
      console.warn(
        `[Raintree] fillRowListRow: label not found for column "${headers[i]}"`
      );
      continue;
    }
    const fieldId = lbl.getAttribute('for');
    if (!fieldId) continue;
    const fieldEl = document.getElementById(fieldId) as HTMLElement | null;
    if (!fieldEl) {
      console.warn(
        `[Raintree] fillRowListRow: element not found for "${headers[i]}" id="${fieldId}"`
      );
      continue;
    }

    if (fieldEl.tagName === 'INPUT' || fieldEl.tagName === 'TEXTAREA') {
      // Row-edit modal is Raintree's native widget system (not React).
      // Must simulate full focus→input→blur sequence so Raintree's change
      // handler picks up the new value before the OK button is clicked.
      const inputEl = fieldEl as HTMLInputElement;
      inputEl.focus();
      inputEl.dispatchEvent(new FocusEvent('focusin', { bubbles: true }));
      nativeInputFill(inputEl, colValue);
      inputEl.dispatchEvent(new FocusEvent('focusout', { bubbles: true }));
      inputEl.blur();
    } else if (fieldEl.tagName === 'SELECT') {
      const sel = fieldEl as HTMLSelectElement;
      const opt = Array.from(sel.options).find(
        (o) =>
          matchesChoice(colValue, o.textContent?.trim() || '') ||
          matchesChoice(colValue, o.value)
      );
      if (opt) {
        sel.value = opt.value;
        sel.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        console.warn(
          `[Raintree] fillRowListRow: no select option match for "${headers[i]}" value="${colValue}"`
        );
      }
    } else if (fieldEl.classList.contains('w-memo-content')) {
      // TinyMCE on popup memo fields (e.g. Goals Comment) uses lazy init — it only
      // activates on a trusted user click, so bubbleFocus/focus won't work.
      // Instead: try setTinyMCEContent immediately; if no editor found, force-init
      // via main_world.js (which clones settings from a live editor) and retry.
      //
      // Also: Raintree's edit modal opens with the memo blank, then async-loads
      // the row's existing comment a few hundred ms later. If we setContent
      // before that load settles, the async load overwrites our value and OK
      // saves the wrong thing. Poll innerHTML stability before setContent.
      const waitForMemoSettle = async (
        el: HTMLElement,
        quietMs: number,
        timeoutMs: number
      ): Promise<void> => {
        let last = el.innerHTML;
        let stableSince = Date.now();
        const start = Date.now();
        while (Date.now() - start < timeoutMs) {
          await new Promise((r) => setTimeout(r, 100));
          const cur = el.innerHTML;
          if (cur !== last) {
            last = cur;
            stableSince = Date.now();
          }
          if (Date.now() - stableSince >= quietMs) return;
        }
      };
      const trySetContent = async (
        id: string,
        value: string
      ): Promise<string> => {
        const reqId = `${id}_${Date.now()}`;
        return new Promise<string>((resolve) => {
          const handler = (e: MessageEvent) => {
            if (
              e.source === window &&
              e.data.action === 'setTinyMCEContent_response' &&
              e.data.reqId === reqId
            ) {
              window.removeEventListener('message', handler);
              resolve(e.data.result as string);
            }
          };
          window.addEventListener('message', handler);
          window.postMessage(
            { action: 'setTinyMCEContent', id, value, reqId },
            '*'
          );
          setTimeout(() => {
            window.removeEventListener('message', handler);
            resolve('timeout');
          }, 2000);
        });
      };

      const forceInitTinyMCE = async (id: string): Promise<string> => {
        const reqId = `init_${id}_${Date.now()}`;
        return new Promise<string>((resolve) => {
          const handler = (e: MessageEvent) => {
            if (
              e.source === window &&
              e.data.action === 'initTinyMCEEditor_response' &&
              e.data.reqId === reqId
            ) {
              window.removeEventListener('message', handler);
              resolve(e.data.result as string);
            }
          };
          window.addEventListener('message', handler);
          window.postMessage({ action: 'initTinyMCEEditor', id, reqId }, '*');
          setTimeout(() => {
            window.removeEventListener('message', handler);
            resolve('timeout');
          }, 4000);
        });
      };

      // Phase 1: ensure TinyMCE has bound the editor for this memo. Try
      // cloning settings from a live editor first; fall back to focus-init
      // polling. Don't setContent yet — Raintree's edit modal async-loads
      // the row's existing comment, and a setContent that fires before the
      // load completes gets clobbered.
      const initResult = await forceInitTinyMCE(fieldEl.id);
      if (initResult === 'no_existing_editors' || initResult === 'no_tinymce') {
        // Focus-init polling: dispatch bubbling focusin + element.focus, wait
        // for contentEditable to flip true (TinyMCE's lazy bind trigger).
        let el: HTMLElement | null = fieldEl;
        bubbleFocus(el);
        el.focus();
        let waited = 0;
        while (el.contentEditable !== 'true' && waited < 3000) {
          await new Promise((r) => setTimeout(r, 200));
          waited += 200;
          el = document.getElementById(fieldEl.id) as HTMLElement | null;
          if (!el) break;
          if (waited % 1000 === 0) {
            bubbleFocus(el);
            el.focus();
          }
        }
      }

      // Phase 2: wait for Raintree's async content-load to settle (600ms with
      // no DOM mutation). Without this, our setContent races the load and the
      // server commit ends up with the original value, not ours.
      await waitForMemoSettle(fieldEl, 600, 4000);

      // Phase 3: single setContent — now the final state of the editor.
      const setResult = await trySetContent(fieldEl.id, colValue);

      if (setResult === 'success') {
        await new Promise((r) => setTimeout(r, 300));
      } else {
        console.warn(
          `[Raintree] fillRowListRow: TinyMCE fill failed (${setResult}), falling back to Widget.HTML`
        );
      }
      // Track the memo element + XHTML so we can commit the edit below
      // (fallback Widget.HTML stamp uses the XHTML, primary path doesn't).
      filledMemoEl = fieldEl;
      filledMemoXHTML = buildRaintreeXHTML(colValue);
    } else {
      // Wrapper element — look for a nested select or input (e.g. combobox wrapper div)
      const nestedSel = fieldEl.querySelector(
        'select'
      ) as HTMLSelectElement | null;
      const nestedInput = fieldEl.querySelector(
        'input'
      ) as HTMLInputElement | null;
      if (nestedSel) {
        const opt = Array.from(nestedSel.options).find(
          (o) =>
            matchesChoice(colValue, o.textContent?.trim() || '') ||
            matchesChoice(colValue, o.value)
        );
        if (opt) {
          nestedSel.value = opt.value;
          nestedSel.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          console.warn(
            `[Raintree] fillRowListRow: no nested select option match for "${headers[i]}" value="${colValue}"`
          );
        }
      } else if (nestedInput) {
        nativeInputFill(nestedInput, colValue);
      } else {
        console.warn(
          `[Raintree] fillRowListRow: unhandled element type for "${headers[i]}" tag=${fieldEl.tagName}`
        );
      }
    }
  }

  // Fill the resolved goal text (with @@ replaced) into the edit form.
  // The Goals text input has no label — find it by locating the input whose
  // current value still contains @@ or <@@> placeholder markers.
  if (goalNameValue) {
    const formInputs = Array.from(
      labelRoot.querySelectorAll('input[type="text"], textarea')
    ) as HTMLInputElement[];
    const atAtInput = formInputs.find((el) => el.value.includes('@@'));
    if (atAtInput) {
      atAtInput.focus();
      atAtInput.dispatchEvent(new FocusEvent('focusin', { bubbles: true }));
      nativeInputFill(atAtInput, goalNameValue);
      atAtInput.dispatchEvent(new FocusEvent('focusout', { bubbles: true }));
      atAtInput.blur();
    } else {
      console.warn(
        '[Raintree] fillRowListRow: no @@ input found in goal edit form'
      );
    }
  }

  await new Promise((r) => setTimeout(r, 300));

  // Real user typing calls inst.setMemoChanged() on each keystroke (marks the
  // modal row dirty so OK propagates to the parent listbox) AND triggers
  // periodic syncs of the diff to the server. Our programmatic setContent does
  // neither, so OK closes the modal with the cell reverting. Drive both
  // explicitly via commitRaintreeMemoEdit, then give the sync ~300ms to land.
  if (filledMemoEl) {
    const committed = commitRaintreeMemoEdit(filledMemoEl);
    if (committed) {
      // Give the diff sync a moment to land on the server before OK closes.
      await new Promise((r) => setTimeout(r, 700));
    } else if (filledMemoXHTML) {
      // Fallback: stamp Widget.HTML on the React fiber prop. Less reliable but
      // better than nothing if the Editor inst lookup fails.
      setRaintreeWidgetHTML(filledMemoEl, filledMemoXHTML);
    }
  }

  // Re-query from document (formContainer may be stale after TinyMCE remount)
  // Click the inner w-image-button (role="button"), not the outer widget wrapper
  const okWrapper = document.querySelector(
    '[data-widgetname="btnok"]'
  ) as HTMLElement | null;
  const okBtn = (okWrapper?.querySelector('.w-image-button') ??
    okWrapper) as HTMLElement | null;
  if (okBtn) {
    okBtn.click();
    // Wait for the modal to actually close (btnok leaves the DOM) before
    // the next row opens its context menu — fixed 500ms is too short and
    // causes the next row to operate against a still-open modal.
    let closeWait = 0;
    while (
      closeWait < 3000 &&
      document.querySelector('[data-widgetname="btnok"]')
    ) {
      await new Promise((r) => setTimeout(r, 100));
      closeWait += 100;
    }
    await new Promise((r) => setTimeout(r, 200));
  } else {
    console.warn('[Raintree] fillRowListRow: btnOK not found');
  }
}

// ─── Utilities ────────────────────────────────────────────────────────────────

/**
 * Find the label for an element: first check label[for=id] within container,
 * then fall back to the provided fallback string.
 */
function resolveLabel(
  el: Element,
  container: Element,
  fallback: string
): string {
  // Normalize raw label text: "Dxcode1_ A" → "Diagnosis Code 1", etc.
  function normalizeLabelText(t: string): string {
    const dx = t.match(/^Dxcode(\d+)/i);
    if (dx) return `Diagnosis Code ${dx[1]}`;
    return t;
  }

  // 1. label[for=id] within container
  const id = el.getAttribute('id');
  if (id) {
    const label = container.querySelector(`label[for="${id}"]`);
    const text = label?.textContent?.trim();
    if (text) return normalizeLabelText(text);
  }
  // 2. aria-label on the element itself
  const aria = el.getAttribute('aria-label')?.trim();
  if (aria) return normalizeLabelText(aria);
  // 3. name attribute → humanize (e.g. "diag1" → "Diagnosis 1", "caseName" → "Case Name")
  const name = el.getAttribute('name')?.trim();
  if (name) {
    const diagMatch = name.match(/^diag(\d+)$/i);
    if (diagMatch) return `Diagnosis ${diagMatch[1]}`;
    // camelCase → Title Case with spaces
    const humanized = name
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (s) => s.toUpperCase())
      .trim();
    if (humanized !== fallback) return humanized;
  }
  // 4. For listboxes: use the first non-checkbox table column header (e.g. "Exercises", "Modalities")
  const firstTh = el.querySelector(
    'thead th:not(.w-listbox-extended-checkbox)'
  );
  const thText = firstTh?.textContent?.trim();
  if (thText) return thText;
  return fallback;
}

/** Strip trailing Raintree section markers like "(S)", "(O)" from panel captions. */
function panelCaptionName(panel: Element): string {
  const text =
    panel.querySelector('.w-panel-caption-text')?.textContent?.trim() || '';
  return text.replace(/\s*\([A-Z]\)\s*$/, '').trim();
}

/** Map Raintree tab data-name values to human-readable section names.
 *  Strips trailing digits so subjective1/subjective2/etc. all map to 'Subjective'. */
function tabDataNameToSectionName(tabName: string): string {
  const base = tabName.replace(/\d+$/, '');
  const map: Record<string, string> = {
    subjective: 'Subjective',
    subj: 'Subjective',
    objective: 'Objective',
    obj: 'Objective',
    assess: 'Assessment',
    plan: 'Plan',
    txplan: 'Treatment Plan',
    progRec: 'Recommendations',
    activities: 'Activities',
    goals: 'Goals'
  };
  return map[base] || map[tabName] || tabName;
}

/** Normalize a key for fuzzy matching: lowercase, strip non-alphanumeric except spaces */
function normalizeKey(key: string): string {
  return key
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Find a value in a Record by exact key, then by normalized key fallback */
function fuzzyLookup<T>(
  record: Record<string, T>,
  key: string
): [string, T] | null {
  if (record[key] !== undefined) return [key, record[key]];
  const normalizedKey = normalizeKey(key);
  for (const [k, v] of Object.entries(record)) {
    if (normalizeKey(k) === normalizedKey) return [k, v];
  }
  return null;
}

function isEmptyOrNull(value: any): boolean {
  if (value === null || value === undefined || value === '') return true;
  if (typeof value === 'string') {
    const lower = value.trim().toLowerCase();
    return lower === 'none' || lower === 'null' || lower === 'n/a';
  }
  return false;
}

function bubbleFocus(element: Element) {
  element.dispatchEvent(
    new FocusEvent('focusin', { bubbles: true, cancelable: true })
  );
}

async function eventBasedFill(element: HTMLElement, value: string) {
  const id = element.id;
  if (id) {
    // If TinyMCE hasn't initialized yet on this element (no user has clicked it),
    // do the same focus-init polling that fillRowListRow uses before trying setContent.
    let targetEl: HTMLElement | null = element;
    if (targetEl.contentEditable !== 'true') {
      bubbleFocus(targetEl);
      targetEl.focus();
      let waited = 0;
      while (targetEl && targetEl.contentEditable !== 'true' && waited < 3000) {
        await new Promise((r) => setTimeout(r, 200));
        waited += 200;
        targetEl = document.getElementById(id) as HTMLElement | null;
        if (targetEl && waited % 1000 === 0) {
          bubbleFocus(targetEl);
          targetEl.focus();
        }
      }
    }

    // Use TinyMCE's API via main world to update its internal model (innerText bypasses it)
    const reqId = `${id}_${Date.now()}`;
    const result = await new Promise<string>((resolve) => {
      const handler = (e: MessageEvent) => {
        if (
          e.source === window &&
          e.data.action === 'setTinyMCEContent_response' &&
          e.data.reqId === reqId
        ) {
          window.removeEventListener('message', handler);
          resolve(e.data.result);
        }
      };
      window.addEventListener('message', handler);
      window.postMessage(
        { action: 'setTinyMCEContent', id, value, reqId },
        '*'
      );
      setTimeout(() => {
        window.removeEventListener('message', handler);
        resolve('timeout');
      }, 2000);
    });
    if (result === 'success') {
      await blurTinyMCEEditor(id);
      return;
    }
  }

  // Fallback: direct DOM manipulation
  element.focus();
  element.click();
  element.innerText = value;
  element.dispatchEvent(new Event('input', { bubbles: true }));
  element.dispatchEvent(new Event('change', { bubbles: true }));
  element.blur();
  element.dispatchEvent(new Event('blur', { bubbles: true }));
}

// Raintree stores memo field content in Widget.HTML as an XHTML document.
// The OK handler reads Widget.HTML directly from the prop, so mutating it
// is sufficient — no events needed.
function buildRaintreeXHTML(text: string): string {
  const esc = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const spanStyle =
    " font-size: 10pt; font-family: 'Arial', 'Helvetica', sans-serif;" +
    ' font-style: normal; font-weight: normal; color: #000000;' +
    ' background-color: transparent; text-decoration: none;';
  const pStyle =
    'text-align: left; text-indent: 0px; padding: 0px 0px 0px 0px; margin: 0px 0px 0px 0px;';
  const lines = text.split('\n');
  const spans = lines
    .map((l) => `<span style="${spanStyle}">${esc(l)}</span>`)
    .join('<br />');
  return (
    '<?xml version="1.0" encoding="UTF-8"?>\r\n' +
    '<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Strict//EN"' +
    ' "http://www.w3.org/TR/xhtml1/DTD/xhtml1-strict.dtd">\r\n' +
    '<html xmlns="http://www.w3.org/1999/xhtml">\r\n' +
    '<head><title>RTF</title>\r\n' +
    '<meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />\r\n' +
    '<meta http-equiv="Content-Style-Type" content="text/css" />\r\n' +
    '</head>\r\n' +
    '<body style="margin: 0px 0px 0px 0px; background-color: #ffffff;">\r\n' +
    `<p style="${pStyle}">${spans}</p>\r\n` +
    '</body></html>\r\n'
  );
}

// After programmatically setting a Raintree memo's content, two operations
// that real user typing performs must be replicated for the edit to survive
// the OK click:
//   1. `inst.memo.doSyncNow()` — flushes the periodic sync timer so the diff
//      reaches the server before OK closes the modal.
//   2. `inst.memo.changed = true` + `inst.setMemoChanged()` — marks the modal
//      row dirty so OK's listbox commit picks it up. Without this, OK closes
//      the modal and the listbox cell reverts even though the diff was synced.
// Walks the React fiber tree directly — DOM expandos like __reactFiber are
// readable from content scripts in Chrome (isolated/main worlds share DOM).
function commitRaintreeMemoEdit(element: HTMLElement): boolean {
  // Walk up the DOM to find the nearest ancestor with a React fiber key.
  // The Activities edit modal mounts w-memo-content without a fiber directly
  // on the element; the fiber lives on a parent wrapper instead.
  let fiberEl: HTMLElement | null = element;
  let fiberKey: string | undefined;
  while (fiberEl && fiberEl !== document.body) {
    fiberKey = Object.keys(fiberEl).find(
      (k) =>
        k.startsWith('__reactFiber') || k.startsWith('__reactInternalInstance')
    );
    if (fiberKey) break;
    fiberEl = fiberEl.parentElement;
  }
  if (!fiberKey || !fiberEl) {
    // Log the ancestor chain to diagnose why no fiber was found
    const chain: string[] = [];
    let dbgEl: HTMLElement | null = element;
    for (let i = 0; i < 12 && dbgEl && dbgEl !== document.body; i++) {
      chain.push(
        `${dbgEl.tagName}${dbgEl.id ? '#' + dbgEl.id : ''}[${dbgEl.className.slice(0, 40)}]`
      );
      dbgEl = dbgEl.parentElement;
    }
    console.warn(
      '[Raintree] commitRaintreeMemoEdit: no fiber key on element; ancestors:',
      chain.join(' > ')
    );
    return false;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let fiber: any = (fiberEl as any)[fiberKey];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let inst: any = null;
  while (fiber) {
    const t = fiber.type;
    const name = (typeof t === 'function' && (t.displayName || t.name)) || null;
    if (name === 'Editor') {
      inst = fiber.stateNode;
      break;
    }
    fiber = fiber.return;
  }
  if (!inst) {
    console.warn('[Raintree] commitRaintreeMemoEdit: no Editor fiber');
    return false;
  }

  let didSync = false;
  let didChanged = false;
  try {
    if (inst.memo && typeof inst.memo.doSyncNow === 'function') {
      inst.memo.doSyncNow();
      didSync = true;
    }
  } catch (e) {
    console.warn('[Raintree] commitRaintreeMemoEdit: doSyncNow threw', e);
  }
  try {
    if (inst.memo) inst.memo.changed = true;
    if (typeof inst.setMemoChanged === 'function') {
      inst.setMemoChanged();
      didChanged = true;
    }
  } catch (e) {
    console.warn('[Raintree] commitRaintreeMemoEdit: setMemoChanged threw', e);
  }
  return didSync && didChanged;
}

function setRaintreeWidgetHTML(element: HTMLElement, html: string): boolean {
  const fiberKey = Object.keys(element).find(
    (k) =>
      k.startsWith('__reactFiber') || k.startsWith('__reactInternalInstance')
  );
  if (!fiberKey) return false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let fiber = (element as any)[fiberKey];
  while (fiber) {
    const mp = fiber.memoizedProps;
    if (mp?.Widget && 'HTML' in mp.Widget) {
      try {
        mp.Widget.HTML = html;
        return true;
      } catch {
        console.warn(
          '[Raintree] setRaintreeWidgetHTML: Widget.HTML not writable'
        );
        return false;
      }
    }
    fiber = fiber.return;
  }
  console.warn(
    '[Raintree] setRaintreeWidgetHTML: Widget with HTML not found on element'
  );
  return false;
}

// Walk the React fiber tree upward from `element`, looking for useState/
// useReducer state that holds an HTML string matching a memo value. When found,
// dispatch an update so Raintree's OK handler reads the new content from state.
function tryUpdateReactFormState(element: HTMLElement, xhtml: string): string {
  const fiberKey = Object.keys(element).find(
    (k) =>
      k.startsWith('__reactFiber') || k.startsWith('__reactInternalInstance')
  );
  if (!fiberKey) return 'no_fiber';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let fiber = (element as any)[fiberKey];
  let depth = 0;
  while (fiber && depth < 40) {
    // Walk the memoizedState hook chain for this fiber
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let ms: any = fiber.memoizedState;
    let hookIdx = 0;
    while (ms) {
      const val = ms.memoizedState;
      // Log any hook state that contains an HTML/xhtml string (for diagnosis)
      if (
        typeof val === 'string' &&
        (val.includes('<?xml') || val.includes('<html') || val.includes('<p>'))
      ) {
        // Try to update this state via the queue dispatcher
        if (ms.queue?.dispatch) {
          try {
            ms.queue.dispatch(xhtml);
            return `dispatched_depth${depth}_hook${hookIdx}`;
          } catch (e) {
            console.warn(
              '[Raintree] tryUpdateReactFormState: dispatch failed',
              e
            );
          }
        }
      }
      // Also check object state with HTML-like fields
      if (val && typeof val === 'object' && !Array.isArray(val)) {
        const keys = Object.keys(val);
        const htmlKey = keys.find(
          (k) =>
            typeof val[k] === 'string' &&
            (val[k].includes('<') ||
              k.toLowerCase().includes('html') ||
              k.toLowerCase().includes('content'))
        );
      }
      ms = ms.next;
      hookIdx++;
    }
    // Class component
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const inst = fiber.stateNode as any;
    if (inst && typeof inst.setState === 'function' && inst.state) {
      const stateKeys = Object.keys(inst.state);
      const htmlKey = stateKeys.find(
        (k) =>
          typeof inst.state[k] === 'string' && inst.state[k].includes('<?xml')
      );
      if (htmlKey) {
        inst.setState({ [htmlKey]: xhtml });
        return `class_setState_depth${depth}`;
      }
    }
    fiber = fiber.return;
    depth++;
  }
  return 'not_found';
}

async function blurTinyMCEEditor(id: string): Promise<void> {
  await new Promise<void>((resolve) => {
    const reqId = `blur_${id}_${Date.now()}`;
    let timer: ReturnType<typeof setTimeout>;
    const handler = (e: MessageEvent) => {
      if (
        e.source === window &&
        e.data.action === 'blurTinyMCE_response' &&
        e.data.reqId === reqId
      ) {
        clearTimeout(timer);
        window.removeEventListener('message', handler);
        resolve();
      }
    };
    window.addEventListener('message', handler);
    window.postMessage({ action: 'blurTinyMCE', id, reqId }, '*');
    timer = setTimeout(() => {
      console.warn('[Raintree] blurTinyMCEEditor: timeout for', id);
      window.removeEventListener('message', handler);
      resolve();
    }, 500);
  });
}

function nativeInputFill(element: HTMLInputElement, value: string) {
  // Use native value setter to bypass React's cached value — then fire a real input event.
  const nativeSetter = Object.getOwnPropertyDescriptor(
    window.HTMLInputElement.prototype,
    'value'
  )?.set;
  if (nativeSetter) {
    nativeSetter.call(element, value);
  } else {
    element.value = value;
  }
  element.dispatchEvent(new InputEvent('input', { bubbles: true }));
  element.dispatchEvent(new Event('change', { bubbles: true }));
}

// ─── Async Mutex ──────────────────────────────────────────────────────────────

class AsyncMutex {
  private chain: Promise<any>;
  constructor() {
    this.chain = Promise.resolve();
  }
  async runExclusive(task: () => Promise<any>) {
    let release: () => void;
    const willLock = new Promise<void>((resolve) => (release = resolve));
    const previous = this.chain;
    this.chain = previous.then(() => willLock);
    await previous;
    try {
      return await task();
    } finally {
      // @ts-ignore release is always assigned above
      release();
    }
  }
}

const questionMutex = new AsyncMutex();

// ─── Answer Functions ─────────────────────────────────────────────────────────

export async function answerQuestions(answers: any[]) {
  answeringInProgress = true;
  const errors = [];
  try {
    for (const answer of answers) {
      const error = await answerQuestion(
        answer['Question'],
        answer['Answer'],
        answer['Reply Type']
      );
      if (error) {
        errors.push({
          Question: answer['Question'],
          Answer: answer['Answer'],
          Error: error
        });
      }
    }
  } finally {
    answeringInProgress = false;
  }
  console.error('Errors:', errors);
  return errors;
}

export async function answerQuestion(
  question: string,
  answer: any,
  replyType = 'overwrite'
) {
  if (!questionInteractions[question]) {
    console.error('Question not found:', question);
    return 'Question not found';
  }

  // Block getQuestions() from switching tabs while any answer is in flight.
  // Use a ref-count so concurrent PUSH_ANSWER calls keep the flag true until all finish.
  answeringInProgress = true;
  __answeringRefCount++;

  beginOverlay('Answering questions…', { title: 'ComprehendPT' });
  pushQuestionToQueue(String(question));

  if (questionInteractions[question].Type === 'Flex') {
    try {
      const flexErrors = await answerFlexQuestion(question, answer, replyType);
      applyAnswerToQuestionCache(question, answer, replyType, flexErrors);
      return true;
    } finally {
      removeQuestionFromQueue(String(question));
      endOverlay();
      __answeringRefCount--;
      if (__answeringRefCount <= 0) {
        __answeringRefCount = 0;
        answeringInProgress = false;
      }
    }
  }

  // Flat Input fallback (should rarely be reached in the flex architecture)
  if (questionInteractions[question].Input) {
    try {
      await questionInteractions[question].Input(answer, replyType);
      return true;
    } finally {
      removeQuestionFromQueue(String(question));
      endOverlay();
      __answeringRefCount--;
      if (__answeringRefCount <= 0) {
        __answeringRefCount = 0;
        answeringInProgress = false;
      }
    }
  }

  __answeringRefCount--;
  if (__answeringRefCount <= 0) {
    __answeringRefCount = 0;
    answeringInProgress = false;
  }
  removeQuestionFromQueue(String(question));
  endOverlay();
  return 'Unhandled question type';
}

function applyAnswerToQuestionCache(
  question: string,
  answer: any,
  replyType: string,
  flexErrors: any
) {
  try {
    // See note above — Objective's existing values are stripped on purpose.
    if (question === 'Objective') return;
    if (!answer || typeof answer !== 'object') return;

    const entry = (cachedQuestions as any[] | null)?.find(
      (q: any) => q?.Key === question
    );
    const structure = entry?.Structure;
    if (!structure || typeof structure !== 'object') return;

    // answerFlexQuestion reports per-field failures; don't record those as written.
    const failed = new Set(
      (Array.isArray(flexErrors) ? flexErrors : [])
        .map((e: any) => e?.Field)
        .filter(Boolean)
    );

    // A plain flex++ leaf only ever carries _existingValue / _choices / _description.
    // Anything else is a pass-through Structure we must not touch.
    const isFlexLeaf = (leaf: any) =>
      leaf &&
      typeof leaf === 'object' &&
      !Array.isArray(leaf) &&
      Object.keys(leaf).every((k) => k.startsWith('_'));

    for (const [fieldKey, fieldDetails] of Object.entries(answer)) {
      if (failed.has(fieldKey)) continue;
      const subsectionMatch = fuzzyLookup(
        structure as Record<string, any>,
        fieldKey
      );
      if (!subsectionMatch) continue;
      const subsectionKey = subsectionMatch[0];
      const subsection = subsectionMatch[1] as Record<string, any>;
      if (!subsection || typeof subsection !== 'object') continue;
      if (Array.isArray(subsection)) continue;

      // Mirror the fill path's re-wrap of a flattened single-field subsection.
      const isDirectAnswer =
        Array.isArray(fieldDetails) ||
        typeof fieldDetails !== 'object' ||
        fieldDetails === null;
      const subFields: Record<string, unknown> = isDirectAnswer
        ? { [subsectionKey]: fieldDetails }
        : (fieldDetails as Record<string, unknown>);

      for (const [subFieldKey, value] of Object.entries(subFields)) {
        if (failed.has(`${fieldKey}.${subFieldKey}`)) continue;
        if (typeof value !== 'string' || isEmptyOrNull(value)) continue;
        const leaf =
          isFlexLeaf(subsection) &&
          normalizeKey(subFieldKey) === normalizeKey(subsectionKey)
            ? subsection
            : (fuzzyLookup(subsection, subFieldKey)?.[1] as any);
        if (!isFlexLeaf(leaf)) continue;
        if (
          typeof leaf._description === 'string' &&
          leaf._description.includes("'$'")
        ) {
          continue;
        }
        const previous = leaf._existingValue;
        leaf._existingValue =
          replyType === 'append' && previous
            ? `${previous}\n${value.trim()}`
            : value.trim();
      }
    }
  } catch (error) {
    // Never let cache bookkeeping break an answer that already landed in the note.
    console.error('Error updating question cache after answer:', error);
  }
}

async function answerFlexQuestion(
  question: string,
  answer: any,
  replyType = 'overwrite'
) {
  return questionMutex.runExclusive(async () => {
    _memoAppendBase.clear();
    _memoAppendedVals.clear();
    try {
      const errors = [];
      // Wait for any in-progress getQuestions scan — it wipes questionInteractions mid-flight
      while (getQuestionsInProgress) {
        await new Promise((r) => setTimeout(r, 100));
      }
      const tabName = questionInteractions[question]?.tabName;

      // Switch to the correct tab before filling so elements are interactive
      const serviceTicketTab = document.querySelector(
        '[data-widgetname="servicetickettab"]'
      );
      if (tabName && serviceTicketTab) {
        const tabEl = serviceTicketTab.querySelector(
          `[data-name="${tabName}"]`
        ) as HTMLElement | null;
        if (tabEl && !tabEl.classList.contains('active')) {
          tabEl.click();
          await new Promise((r) => setTimeout(r, 600));
        }
      }

      // Raintree re-renders tab DOM on every tab switch — rebuild structure with fresh element refs.
      // Re-register qi each iteration in case getQuestions() wiped questionInteractions mid-flight.
      // Poll until panels appear (up to 2s) to handle lazy-loading tabs.
      const tabContent = serviceTicketTab?.querySelector('#tabcontent');
      let freshStructure: Record<string, any> = {};
      if (tabContent) {
        let attempts = 0;
        while (attempts < 8) {
          // Re-click our tab — getQuestions() may have switched away during our await
          if (tabName) {
            const tabEl = serviceTicketTab?.querySelector(
              `[data-name="${tabName}"]`
            ) as HTMLElement | null;
            if (tabEl && !tabEl.classList.contains('active')) {
              tabEl.click();
              await new Promise((r) => setTimeout(r, 400));
            }
          }

          freshStructure = {};
          questionInteractions[question] = {
            Type: 'Flex',
            Key: question,
            Question: question,
            tabName,
            Structure: freshStructure
          };
          await buildAllPanels(tabContent, question, true);

          // Verify we got the right tab's content before accepting the structure
          const activeTabName = (
            serviceTicketTab?.querySelector(
              '.tab-item.active'
            ) as HTMLElement | null
          )?.dataset.name;
          if (
            activeTabName === tabName &&
            Object.keys(freshStructure).length > 0
          )
            break;

          await new Promise((r) => setTimeout(r, 300));
          attempts++;
        }
        // Wait for TinyMCE to initialize after bubbleFocus was dispatched during buildPanelStructure
        await new Promise((r) => setTimeout(r, 2000));
      }

      const structure = freshStructure;
      const rebuiltSubTabs = new Set<string>();

      for (const [fieldKey, fieldDetails] of Object.entries(answer)) {
        // If this subsection is from a nested sub-tab (Phase 2), skipPhase2=true means it wasn't
        // rebuilt above. Navigate to the sub-tab now and rebuild just that part with fresh refs.
        const registryKey =
          Array.from(_subTabRegistry.keys()).find((k) => {
            const [rQuestion, rSection] = k.split('::');
            return (
              rQuestion === question &&
              normalizeKey(rSection) === normalizeKey(fieldKey)
            );
          }) ?? null;
        if (registryKey) {
          const entry = _subTabRegistry.get(registryKey)!;
          const dedupKey = `${entry.widgetName}:${entry.tabName}`;
          if (!rebuiltSubTabs.has(dedupKey) && tabContent) {
            rebuiltSubTabs.add(dedupKey);
            const widgetEl = tabContent.querySelector(
              `[data-widgetname="${entry.widgetName}"]`
            );
            if (widgetEl) {
              const ctrl = widgetEl.querySelector('.w-tab-control');
              const tabItem = (
                Array.from(
                  ctrl?.querySelectorAll('.w-tab-control-tabs .tab-item') ?? []
                ) as HTMLElement[]
              ).find(
                (t) =>
                  (t.getAttribute('aria-label')?.trim() ||
                    t.querySelector('span')?.textContent?.trim()) ===
                  entry.tabName
              );
              if (tabItem && !tabItem.classList.contains('active')) {
                tabItem.click();
                await new Promise((r) => setTimeout(r, 400));
              }
              // Re-query after tab click
              const freshCtrl = widgetEl.querySelector('.w-tab-control');
              const freshTabContent = freshCtrl?.querySelector(
                '.w-tab-control-content'
              ) as HTMLElement | null;
              const panelsInTab = freshTabContent
                ? Array.from(freshTabContent.querySelectorAll('.w-panel'))
                : [];
              if (freshTabContent) {
                for (const panel of panelsInTab) {
                  const pName = panelCaptionName(panel);
                  if (!pName) continue;
                  await buildPanelStructure(panel, question);
                }
                await buildPanelStructure(
                  freshTabContent,
                  question,
                  entry.tabName
                );
              }
            }
          }
        }

        const subsectionMatch = fuzzyLookup(
          structure as Record<string, any>,
          fieldKey
        );
        if (!subsectionMatch) {
          console.warn(
            `[Raintree] Subsection not found: "${fieldKey}". Available:`,
            Object.keys(structure)
          );
          errors.push({
            Field: fieldKey,
            Error: 'Subsection not found in Structure'
          });
          continue;
        }
        const subsectionKey = subsectionMatch[0];
        const subsection = subsectionMatch[1] as Record<string, any>;

        // Flattened single-field subsection: the AI answered with a direct value (array or
        // primitive) instead of { subFieldKey: value }. Re-wrap it so the field lookup below
        // finds the right Input function — the internal structure still has the full nesting.
        // Indexed dict {"0": {...}, "1": {...}} → array: row-list fields sent as nested lists
        // are converted to indexed dict by from_comprehend_to_openai; convert back here.
        let normalizedFieldDetails: unknown = fieldDetails;
        if (
          !Array.isArray(fieldDetails) &&
          typeof fieldDetails === 'object' &&
          fieldDetails !== null &&
          Object.keys(fieldDetails as object).length > 0 &&
          Object.keys(fieldDetails as object).every((k) => /^\d+$/.test(k))
        ) {
          normalizedFieldDetails = Object.keys(
            fieldDetails as Record<string, unknown>
          )
            .sort((a, b) => parseInt(a) - parseInt(b))
            .map((k) => (fieldDetails as Record<string, unknown>)[k]);
        }

        const isDirectAnswer =
          Array.isArray(normalizedFieldDetails) ||
          typeof normalizedFieldDetails !== 'object' ||
          normalizedFieldDetails === null;
        const subFields: Record<string, unknown> = isDirectAnswer
          ? { [subsectionKey]: normalizedFieldDetails }
          : (normalizedFieldDetails as Record<string, unknown>);

        for (const [subFieldKey, value] of Object.entries(subFields)) {
          const fieldMatch = fuzzyLookup(subsection, subFieldKey);
          const field = fieldMatch?.[1] as any;
          if (!field?.Input) {
            console.warn(
              `[Raintree] Input not found: "${fieldKey}.${subFieldKey}". Available:`,
              Object.keys(subsection)
            );
            errors.push({
              Field: `${fieldKey}.${subFieldKey}`,
              Error: 'Input function not found'
            });
            continue;
          }

          // Row-list fields (Goals, Chief Complaints): AI responds with an array of row objects
          // OR { rows: [...] }. Queue each row individually for the batch flush.
          const rowArray: any[] | null = Array.isArray(value)
            ? (value as any[])
            : Array.isArray((value as any)?.rows)
              ? (value as any).rows
              : null;
          if (rowArray !== null) {
            for (const rowObj of rowArray) {
              try {
                await field.Input(rowObj, replyType);
              } catch (err) {
                console.error(
                  `[Raintree] Error filling row "${fieldKey}.${subFieldKey}":`,
                  err
                );
                errors.push({
                  Field: `${fieldKey}.${subFieldKey}`,
                  Error: `Error applying row: ${String(err)}`
                });
              }
            }
            continue;
          }

          // Only split on '$' for multi-select fields (pickers, checkbox listboxes).
          // Memo/text fields use the whole string — splitting would overwrite on each call.
          const isMultiSelect =
            typeof field.Description === 'string' &&
            field.Description.includes("'$'");
          const values =
            isMultiSelect && typeof value === 'string'
              ? value
                  .split('$')
                  .map((v: string) => v.trim())
                  .filter(Boolean)
              : [value];

          for (const val of values) {
            try {
              await field.Input(val, replyType);
            } catch (err) {
              console.error(
                `[Raintree] Error filling "${fieldKey}.${subFieldKey}":`,
                err
              );
              errors.push({
                Field: `${fieldKey}.${subFieldKey}`,
                Error: `Error applying value: ${String(err)}`
              });
            }
          }
        }
      }

      // Flush any batched picker selections (accumulated across all Input calls above)
      for (const [key, flush] of Array.from(_pickerBatchFlushers.entries())) {
        const pending = _pickerBatch.get(key)?.length ?? 0;
        if (pending > 0) {
          await flush();
        }
      }

      if (errors.length > 0) console.error('[Raintree] Fill errors:', errors);
      return errors.length > 0 ? errors : null;
    } catch (err) {
      console.error('Unexpected error in answerFlexQuestion:', err);
      return { Question: question, Error: `Unexpected error: ${String(err)}` };
    }
  });
}

export async function getLogFromInjector() {
  try {
    const htmlContent = document.documentElement.outerHTML;
    chrome.runtime.sendMessage({
      type: 'INJECTOR_ERROR',
      error: { htmlContent }
    });
    return { htmlContent };
  } catch (error) {
    console.error('Error in getLogFromInjector:', error);
    getLogFromInjector();
  }
}

// ─── Blur Overlay ─────────────────────────────────────────────────────────────

const OVERLAY_ID = 'BlurOverlay';
const ACTIVE_CLASS = 'blur-overlay-active';
const CSS_ID = 'BlurOverlayStyles';

let overlayEl: HTMLElement | null = null;
let msgEl: HTMLElement | null = null;
let closeBtn: HTMLElement | null = null;
let lastFocused: HTMLElement | null = null;
let _overlayAutoCloseTimer: ReturnType<typeof setTimeout> | null = null;
const OVERLAY_AUTO_CLOSE_MS = 240_000; // 4-minute safety net

function injectStyles() {
  if (document.getElementById(CSS_ID)) return;
  const style = document.createElement('style');
  style.id = CSS_ID;
  style.textContent = `
     html.${ACTIVE_CLASS} { overflow: hidden; }
     body.${ACTIVE_CLASS} { overflow: hidden; }

     html.${ACTIVE_CLASS} body > *:not(#${OVERLAY_ID}) {
       filter: blur(var(--blur-overlay-amount, 8px)) saturate(0.9);
       pointer-events: none;
       user-select: none;
     }

     #${OVERLAY_ID} {
       position: fixed;
       inset: 0;
       --brand-color: rgb(107, 216, 136);
       z-index: 2147483647;
       display: none;
       align-items: center;
       justify-content: center;
       padding: 24px;
     }

     #${OVERLAY_ID}[data-open="true"] { display: flex; }

     #${OVERLAY_ID}::before {
       content: "";
       position: absolute;
       inset: 0;
       background: rgba(0,0,0,0.25);
       backdrop-filter: blur(var(--blur-overlay-amount, 8px)) brightness(0.95);
       -webkit-backdrop-filter: blur(var(--blur-overlay-amount, 8px)) brightness(0.95);
     }

     #${OVERLAY_ID} .blur-msg {
       position: relative;
       max-width: min(720px, 90vw);
       width: max(320px, 40vw);
       background: rgba(255,255,255,0.9);
       border-radius: 16px;
       box-shadow: 0 10px 30px rgba(0,0,0,0.2);
       padding: 20px 24px;
       font-family: system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif;
       color: #111;
       backdrop-filter: blur(2px);
     }

     #${OVERLAY_ID} .blur-msg h2 {
       margin: 0 0 10px 0;
       font-size: 18px;
       font-weight: 600;
       color: var(--brand-color);
     }

     #${OVERLAY_ID} .blur-msg .blur-text {
       line-height: 1.5;
       font-size: 15px;
       white-space: pre-wrap;
     }

     #${OVERLAY_ID} .blur-close {
       position: fixed;
       top: 12px;
       right: 12px;
       appearance: none;
       border: none;
       background: var(--brand-color);
       color: #fff;
       width: 36px;
       height: 36px;
       border-radius: 10px;
       font-size: 20px;
       line-height: 36px;
       text-align: center;
       cursor: pointer;
       box-shadow: 0 4px 12px rgba(0,0,0,0.25);
     }
     #${OVERLAY_ID} .blur-close:hover { background: #00937f; }
     #${OVERLAY_ID} .blur-close:focus { outline: 2px solid #fff; outline-offset: 2px; }
   `;
  document.head.appendChild(style);
}

function createOverlay() {
  if (document.getElementById(OVERLAY_ID)) {
    overlayEl = document.getElementById(OVERLAY_ID);
    msgEl = overlayEl.querySelector('.blur-text');
    closeBtn = overlayEl.querySelector('.blur-close');
    return;
  }
  overlayEl = document.createElement('div');
  overlayEl.id = OVERLAY_ID;
  overlayEl.setAttribute('aria-hidden', 'true');

  const card = document.createElement('div');
  card.className = 'blur-msg';
  card.setAttribute('role', 'dialog');
  card.setAttribute('aria-modal', 'true');
  card.setAttribute('aria-live', 'polite');

  const title = document.createElement('h2');
  title.textContent = 'Notice';

  msgEl = document.createElement('div');
  msgEl.className = 'blur-text';
  msgEl.textContent = '';

  closeBtn = document.createElement('button');
  closeBtn.className = 'blur-close';
  closeBtn.setAttribute('type', 'button');
  closeBtn.setAttribute('aria-label', 'Close overlay');
  closeBtn.textContent = '×';

  card.appendChild(title);
  card.appendChild(msgEl);
  overlayEl.appendChild(card);
  overlayEl.appendChild(closeBtn);
  document.body.appendChild(overlayEl);

  closeBtn.addEventListener('click', hide);
  document.addEventListener('keydown', (e) => {
    if (overlayEl?.dataset.open === 'true' && e.key === 'Escape') hide();
  });
}

function show(message = '', opts: any = {}) {
  injectStyles();
  createOverlay();

  const {
    title = 'Notice',
    blurAmount = 8,
    textColor,
    background,
    width
  } = opts;

  overlayEl.style.setProperty('--blur-overlay-amount', String(blurAmount));
  const card = overlayEl.querySelector('.blur-msg') as HTMLElement;
  if (textColor) card.style.color = textColor;
  if (background) card.style.background = background;
  if (width) card.style.width = width;

  (overlayEl.querySelector('h2') as HTMLElement).textContent = title;
  msgEl.textContent = message ?? '';

  document.documentElement.classList.add(ACTIVE_CLASS);
  document.body.classList.add(ACTIVE_CLASS);

  closeBtn.style.display = '';
  overlayEl.dataset.open = 'true';
  overlayEl.setAttribute('aria-hidden', 'false');

  lastFocused =
    document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
  (closeBtn as HTMLElement).focus();

  // Safety net: force-close if the overlay is somehow still open after 4 minutes.
  if (_overlayAutoCloseTimer) clearTimeout(_overlayAutoCloseTimer);
  _overlayAutoCloseTimer = setTimeout(() => {
    _overlayAutoCloseTimer = null;
    if (isVisible()) {
      __overlayRefCount = 0;
      hide();
    }
  }, OVERLAY_AUTO_CLOSE_MS);
}

function update(message = '') {
  if (!overlayEl) createOverlay();
  msgEl.textContent = message ?? '';
}

function hide() {
  if (!overlayEl) return;
  overlayEl.dataset.open = 'false';
  overlayEl.setAttribute('aria-hidden', 'true');
  document.documentElement.classList.remove(ACTIVE_CLASS);
  document.body.classList.remove(ACTIVE_CLASS);
  if (_overlayAutoCloseTimer) {
    clearTimeout(_overlayAutoCloseTimer);
    _overlayAutoCloseTimer = null;
  }
  if (lastFocused && document.contains(lastFocused)) {
    try {
      lastFocused.focus();
    } catch {}
  }
  lastFocused = null;
}

function isVisible() {
  return !!(overlayEl && overlayEl.dataset.open === 'true');
}

// ─── Overlay ref-count helpers ────────────────────────────────────────────────

let __overlayRefCount = 0;

function beginOverlay(message: string, opts?: any) {
  __overlayRefCount++;
  if (!isVisible()) {
    show(message, opts);
  } else {
    update(message);
  }
}

function endOverlay() {
  __overlayRefCount = Math.max(0, __overlayRefCount - 1);
  if (__overlayRefCount === 0 && isVisible()) {
    hide();
  }
}

// ─── Queue tracking ───────────────────────────────────────────────────────────

let __overlayAnswerQueue: string[] = [];

function formatAnswerQueueMessage() {
  const header = 'Finishing your note… This should only take a few seconds.';
  if (__overlayAnswerQueue.length === 0) return header;
  const current = __overlayAnswerQueue[0];
  const nextItems = __overlayAnswerQueue.slice(1);
  const nextPreview = nextItems.slice(0, 3);
  const remaining = Math.max(0, nextItems.length - nextPreview.length);
  let message = header + `\nCurrently working on: ${current}`;
  if (nextPreview.length > 0) {
    message +=
      `\nUp next: ${nextPreview.join(', ')}` +
      (remaining > 0 ? `, and ${remaining} more` : '');
  }
  return message;
}

function pushQuestionToQueue(questionLabel: string) {
  if (!questionLabel) return;
  if (!__overlayAnswerQueue.includes(questionLabel)) {
    __overlayAnswerQueue.push(questionLabel);
  }
  if (isVisible()) update(formatAnswerQueueMessage());
}

function removeQuestionFromQueue(questionLabel: string) {
  const idx = __overlayAnswerQueue.indexOf(questionLabel);
  if (idx !== -1) __overlayAnswerQueue.splice(idx, 1);
  if (isVisible()) update(formatAnswerQueueMessage());
  // Refresh the getQuestions cache TTL as each answer finishes, so a scan triggered right after
  // answering serves the cache instead of re-walking every tab (which could clobber fresh fills).
  lastGetQuestionsAt = Date.now();
}
